'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import {
    Sparkles,
    X,
    Send,
    Camera,
    Image as ImageIcon,
    FileText,
    Mic,
    MicOff,
    Volume2,
    VolumeX,
    ArrowRightLeft,
    CheckCircle2,
    ShieldCheck,
    Calculator,
    Compass,
    Loader2,
    Clock,
    SquarePen,
    Trash2,
    MessageSquare,
} from 'lucide-react';
import { useCorporate } from '@/context/CorporateContext';
import toast from 'react-hot-toast';

interface CopilotTurnMessage {
    id: string;
    role: 'user' | 'assistant';
    text: string;
    spokenText?: string;
    timestamp: string;
    imagePreviewUrl?: string;
    attachmentMimeType?: string;
    attachmentFileName?: string;
    intentCategory?: string;
    disambiguationQuestions?: string[];
    suggestedFollowUps?: string[];
    loanSimulationCard?: {
        eligible?: boolean;
        availableCreditLimitArsHuman: string;
        requestedPrincipalArsHuman: string;
        tnaPercent: string;
        recommendedInstallments: number;
        simulationMode?:
            | 'PRE_APPROVED'
            | 'DATA_COLLECTION_REQUIRED'
            | 'AMOUNT_SELECTION_REQUIRED'
            | 'JABA_SCORING_REVIEW_REQUIRED'
            | 'INFORMATIVE_ONLY';
        missingFields?: Array<{
            fieldKey: 'cuit_cuil' | 'phone' | 'declared_monthly_income' | 'domicilio';
            label: string;
            placeholder: string;
            helperText?: string;
            example?: string;
        }>;
        verifiedFields?: Array<{
            fieldKey?: string;
            label: string;
            value: string;
        }>;
        savedFieldsInTurn?: Array<{
            fieldKey?: string;
            label: string;
            value: string;
        }>;
        jabaScoringDetail?: {
            providerCode: string;
            externalPersonId: string | number;
            fullName: string;
            cuit: string;
            scoreBcra: number | null;
            indicePredictor: string | null;
            bancarizado: boolean;
            ingresoPredictorHuman: string;
            ingresoCalculadoJabaHuman: string;
            cupoDisponibleTeoricoHuman: string;
            deudaTotalBcraHuman: string;
            porcentajeOcupacionPct: number;
            limiteFinalJabaHuman: string;
        } | null;
        plans: Array<{
            installments: number;
            monthlyInstallmentArsHuman: string;
            totalRepaymentArsHuman: string;
            cftEstimatedPercent: string;
            isRecommended: boolean;
        }>;
    } | null;
    preparedPaymentCard?: {
        recipientAliasOrCvu: string;
        recipientName: string;
        targetCurrency: string;
        requiredAmountHuman: string;
        currentTargetBalanceHuman: string;
        missingTargetAmountHuman: string;
        needsAutoSwap: boolean;
        swapSourceCurrency: string;
        swapSourceAmountRequiredHuman: string;
        appliedFxRateHuman: string;
        rail: string;
        concept: string;
        subaccountLabel: 'MAIN' | 'AGENT_EXPENSE_BOX';
    } | null;
    financialReportCard?: {
        generatedAtIso: string;
        focusedAssetSymbol: string | null;
        portfolioValuationUsdHuman: string;
        portfolioValuationArsHuman: string;
        appliedUsdArsRateHuman: string;
        currentMonth: {
            periodLabel: string;
            totalOperationsCount: number;
            outgoingTransfersCount: number;
            incomingTransfersCount: number;
            externalDepositsCount: number;
            swapsCount: number;
            merchantPaymentsCount: number;
        };
        previousMonth: {
            periodLabel: string;
            totalOperationsCount: number;
            outgoingTransfersCount: number;
            incomingTransfersCount: number;
            externalDepositsCount: number;
            swapsCount: number;
            merchantPaymentsCount: number;
        };
        comparisonHeadline: string;
        focusedAssetHeadline: string | null;
        currencyBreakdowns: Array<{
            currency: string;
            displayName: string;
            currentBalanceMainHuman: string;
            currentBalanceAgentBoxHuman: string;
            valuationUsdHuman: string;
            earnApyPct: string;
            currentMonthTotalOpsCount: number;
            currentMonthSentCount: number;
            currentMonthSentHuman: string;
            currentMonthReceivedCount: number;
            currentMonthReceivedHuman: string;
            currentMonthNetFlowHuman: string;
            previousMonthTotalOpsCount: number;
            previousMonthSentCount: number;
            previousMonthSentHuman: string;
            previousMonthReceivedCount: number;
            previousMonthReceivedHuman: string;
            allTimeTotalOpsCount: number;
        }>;
        proactiveRecommendations: Array<{
            id: string;
            category: string;
            title: string;
            rationale: string;
            actionLabel: string;
            actionPrompt: string;
            impactHighlight: string;
        }>;
    } | null;
}

interface CopilotChatSession {
    id: string;
    title: string;
    updatedAt: string;
    screenLabel: string;
    messages: CopilotTurnMessage[];
}

function hasPositiveHumanAmount(val?: string | null): boolean {
    if (!val) return false;
    const cleaned = String(val).trim().replace(/[^0-9]/g, '');
    return cleaned.length > 0 && !/^0+$/.test(cleaned);
}

function formatLocaleHumanAmount(val?: string | null): string {
    const raw = String(val || '0').trim();
    const [intPartRaw = '0', decPartRaw] = raw.split('.');
    const cleanInt = intPartRaw.replace(/[^0-9-]/g, '') || '0';
    const withThousands = cleanInt.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    if (decPartRaw && !/^0+$/.test(decPartRaw)) {
        return `${withThousands},${decPartRaw.slice(0, 2)}`;
    }
    return withThousands;
}

interface LiveDomScreenSnapshot {
    activeSubTab: string;
    pageHeading: string;
    sectionHeading: string;
    metricsSummary: string;
}

interface ScreenContextMeta {
    title: string;
    baseTitle: string;
    activeSubTab: string;
    phrase: string;
    portalRole: 'WALLET' | 'MERCHANT' | 'MASTER';
    welcomeText: string;
    quickPrompts: string[];
    contextKey: string;
}

function extractLiveDomScreenSnapshot(): LiveDomScreenSnapshot {
    if (typeof document === 'undefined') {
        return { activeSubTab: '', pageHeading: '', sectionHeading: '', metricsSummary: '' };
    }
    try {
        const mainEl = document.querySelector('main') || document.body;
        const pageH1 = mainEl.querySelector('h1')?.textContent?.replace(/\s+/g, ' ').trim() || '';
        const pageH2 = mainEl.querySelector('h2')?.textContent?.replace(/\s+/g, ' ').trim() || '';
        const pageHeading = pageH1 || pageH2;
        const sectionHeading = mainEl.querySelector('h3')?.textContent?.replace(/\s+/g, ' ').trim() || '';

        let activeSubTab = '';

        // 1. Check explicit ARIA or data attributes
        const ariaTab = mainEl.querySelector('[role="tab"][aria-selected="true"], [data-state="active"]');
        if (ariaTab?.textContent) {
            activeSubTab = ariaTab.textContent.replace(/\s+/g, ' ').trim();
        }

        // 2. Scan horizontal tab button groups inside <main>
        if (!activeSubTab) {
            const candidateContainers = Array.from(mainEl.querySelectorAll('div'));
            for (const container of candidateContainers) {
                if (container.closest('[data-max-copilot-drawer="true"]')) continue;
                const directButtons = Array.from(container.children).filter(
                    (el): el is HTMLButtonElement => el.tagName === 'BUTTON'
                );
                if (directButtons.length < 2 || directButtons.length > 7) continue;

                // Pattern A: Bottom-border tabs (border-b-2 where active tab does not have border-transparent)
                const borderTabActive = directButtons.find(
                    (btn) =>
                        btn.className.includes('border-b-2') &&
                        !btn.className.includes('border-transparent')
                );
                const borderTabInactive = directButtons.some(
                    (btn) =>
                        btn.className.includes('border-b-2') &&
                        btn.className.includes('border-transparent')
                );
                if (borderTabActive && borderTabInactive) {
                    activeSubTab = (borderTabActive.textContent || '').replace(/\s+/g, ' ').trim();
                    break;
                }

                // Pattern B: Solid colored pill tabs (bg-blue-600 / bg-purple-600 / bg-emerald-600 / bg-indigo-600 / bg-slate-900 + text-white)
                const solidActive = directButtons.find((btn) =>
                    /(bg-blue-600|bg-purple-600|bg-emerald-600|bg-indigo-600|bg-slate-900|bg-cyan-500)/.test(
                        btn.className
                    ) && btn.className.includes('text-white')
                );
                const solidInactive = directButtons.some(
                    (btn) => !btn.className.includes('text-white')
                );
                if (solidActive && solidInactive) {
                    activeSubTab = (solidActive.textContent || '').replace(/\s+/g, ' ').trim();
                    break;
                }

                // Pattern C: Segmented control container (bg-slate-100 where active button has bg-white and shadow-sm)
                if (container.className.includes('bg-slate-100') || container.className.includes('bg-slate-900')) {
                    const segActive = directButtons.find(
                        (btn) => btn.className.includes('bg-white') && btn.className.includes('shadow')
                    );
                    const segInactive = directButtons.some(
                        (btn) => !btn.className.includes('bg-white')
                    );
                    if (segActive && segInactive) {
                        activeSubTab = (segActive.textContent || '').replace(/\s+/g, ' ').trim();
                        break;
                    }
                }
            }
        }

        // Clean trailing counts like "(12)" from the display sub-tab while keeping it readable
        const cleanSubTab = activeSubTab.replace(/\s*\(\d+\)\s*$/, '').trim();

        // Extract up to 6 visible KPI / card snippets from <main>
        const metricNodes = Array.from(
            mainEl.querySelectorAll('.liquid-glass-card-light, .glass-card-obsidian, [data-kpi-card]')
        ).slice(0, 6);
        const metricParts: string[] = [];
        for (const node of metricNodes) {
            if (node.closest('[data-max-copilot-drawer="true"]')) continue;
            const txt = (node.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 140);
            if (txt) metricParts.push(txt);
        }

        const metricsSummary = [
            pageHeading ? `Título de pantalla: ${pageHeading}` : '',
            cleanSubTab ? `Pestaña interna activa: ${cleanSubTab}` : '',
            sectionHeading ? `Sección visible: ${sectionHeading}` : '',
            metricParts.length > 0 ? `Indicadores en pantalla: ${metricParts.join(' | ')}` : '',
        ]
            .filter(Boolean)
            .join(' · ');

        return {
            activeSubTab: cleanSubTab,
            pageHeading,
            sectionHeading,
            metricsSummary,
        };
    } catch {
        return { activeSubTab: '', pageHeading: '', sectionHeading: '', metricsSummary: '' };
    }
}

export function resolveScreenTitle(
    pathname: string,
    activeSubTab = '',
    copilotName = 'Max'
): ScreenContextMeta {
    const p = (pathname || '').toLowerCase();
    const subLower = (activeSubTab || '').toLowerCase();
    const withSubTab = (base: string) =>
        activeSubTab && !base.toLowerCase().includes(subLower) ? `${base} · ${activeSubTab}` : base;

    // =========================================================================
    // 1. PORTAL CONSOLA MASTER (/master/*)
    // =========================================================================
    if (p.startsWith('/master')) {
        if (p.includes('/tenants') || p.includes('/corporates') || p.includes('/ecosystem')) {
            const title = withSubTab('Multi-Tenant Hub');
            return {
                title,
                baseTitle: 'Multi-Tenant Hub',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo en vivo el ${title}. Puedo detallarte los tenants activos, sus dominios personalizados, estado de bóveda criptográfica y configuración de marca blanca.`,
                quickPrompts: [
                    '¿Cuántos tenants tenemos activos y cuáles son sus dominios?',
                    '¿Qué tenants tienen bóveda criptográfica y pasarelas activas?',
                    '¿Cómo configuro o audito un tenant marca blanca?',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/gateways')) {
            const title = withSubTab('Pasarelas & 4-Eyes');
            return {
                title,
                baseTitle: 'Pasarelas & 4-Eyes',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy situado en ${title}. Puedo ayudarte a auditar las pasarelas de cobro activas por tenant, el sellado de secretos en bóveda y las aprobaciones de control dual (4-Eyes).`,
                quickPrompts: [
                    '¿Qué pasarelas y rieles de cobro están activos hoy?',
                    '¿Cómo funciona el sellado de credenciales y la aprobación 4-Eyes?',
                    'Ver estado de proveedores configurados por tenant',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/treasury') || p.includes('/asignacion') || p.includes('/distribucion')) {
            const title = withSubTab('Tesorería & Recaudadoras');
            const subPrompts = subLower.includes('2') || subLower.includes('recaudadora')
                ? [
                      'Resumime el estado de las cuentas recaudadoras y conciliación fiat',
                      '¿Cómo está la segregación entre fondos de clientes y patrimonio propio?',
                      'Ver últimos movimientos y barridos de tesorería',
                  ]
                : subLower.includes('3') || subLower.includes('on-chain') || subLower.includes('cripto')
                ? [
                      'Resumime la liquidez on-chain en USDT y ETH',
                      '¿Hay barridos físicos pendientes de conciliación?',
                      'Verificar paridad entre saldos contables y reservas',
                  ]
                : [
                      'Resumime la posición global de Tesorería y liquidez por moneda',
                      '¿Cómo está la segregación patrimonial de cuentas de clientes?',
                      'Analizar movimientos y flujo neto de este mes vs. el mes pasado',
                  ];
            return {
                title,
                baseTitle: 'Tesorería & Recaudadoras',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo analizar en tiempo real la liquidez por moneda, cuentas recaudadoras, segregación patrimonial y conciliación de fondos.`,
                quickPrompts: subPrompts,
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/agents')) {
            const title = withSubTab('Asistentes & Auto-Earn');
            return {
                title,
                baseTitle: 'Asistentes & Auto-Earn',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Desde acá podés supervisar los agentes autónomos enrolados, los topes de gasto por mandato, las subcuentas separadas y las autorizaciones pendientes.`,
                quickPrompts: [
                    '¿Cuál es el estado de los agentes autónomos y mandatos activos?',
                    '¿Cómo funcionan las subcuentas separadas y el bloqueo preventivo?',
                    'Resumime las operaciones ejecutadas por agentes este mes',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/settlements')) {
            const title = withSubTab('Liquidaciones & Clearing');
            const subPrompts = subLower.includes('deuda') || subLower.includes('debt')
                ? [
                      '¿Existen acreencias o deudas pendientes de compensación?',
                      '¿Cómo opera la protección de saldo no negativo en reversas?',
                      'Resumime las órdenes liquidadas este mes',
                  ]
                : subLower.includes('clearing')
                ? [
                      '¿Cuál es el estado de las cuentas puente de Clearing?',
                      'Verificar que todas las liquidaciones cierren en suma cero',
                      'Comparar volumen liquidado este mes vs. el mes pasado',
                  ]
                : [
                      '¿Qué órdenes y liquidaciones se procesaron este mes?',
                      'Comparame el volumen de transferencias y pagos contra el mes pasado',
                      '¿Hay órdenes pendientes de confirmación externa?',
                  ];
            return {
                title,
                baseTitle: 'Liquidaciones & Clearing',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo detallarte el libro de órdenes, el estado de clearing multi-moneda y cualquier acreencia pendiente.`,
                quickPrompts: subPrompts,
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/config') || p.includes('/settings')) {
            const title = withSubTab('Parámetros Globales');
            const subPrompts = subLower.includes('oráculo') || subLower.includes('oraculo') || subLower.includes('oracle')
                ? [
                      '¿A cuánto están cotizando hoy los oráculos USD/ARS, USDT y ETH?',
                      '¿Cada cuánto se actualizan las cotizaciones de referencia?',
                      'Ver impacto de cotizaciones en la valuación del portafolio',
                  ]
                : subLower.includes('regul')
                ? [
                      '¿Cuáles son los topes regulatorios UIF/KYC vigentes por nivel?',
                      'Verificar parámetros de cumplimiento y retención probatoria',
                      '¿Está activo el modo mantenimiento o algún kill-switch?',
                  ]
                : [
                      '¿Cómo están cotizando los oráculos USD/ARS y ETH/USD en vivo?',
                      '¿Qué proveedores están activos en KYC, Custodia y Fiat?',
                      'Verificar estado operativo global de la plataforma',
                  ];
            return {
                title,
                baseTitle: 'Parámetros Globales',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo informarte sobre las cotizaciones en vivo de los oráculos, parámetros regulatorios y estado de los proveedores de infraestructura.`,
                quickPrompts: subPrompts,
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/notifications')) {
            const title = withSubTab('Notificaciones E2E');
            return {
                title,
                baseTitle: 'Notificaciones E2E',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy situado en ${title}. Puedo ayudarte a revisar campañas, plantillas transaccionales, trazabilidad probatoria de envíos y salud de despacho.`,
                quickPrompts: [
                    '¿Cómo funcionan las notificaciones regulatorias obligatorias?',
                    'Ver estado de despacho y trazabilidad del canal probatorio',
                    '¿Qué plantillas y alertas transaccionales están activas?',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/products') || p.includes('/pricing') || p.includes('/fees') || p.includes('/limits')) {
            const title = withSubTab('Activos & Comisiones');
            const subPrompts = subLower.includes('regla') || subLower.includes('rule') || subLower.includes('comis')
                ? [
                      '¿Qué reglas de comisión y liquidación están activas?',
                      '¿Cómo se contabilizan las comisiones en partida doble?',
                      'Analizar volumen operado por moneda este mes',
                  ]
                : [
                      '¿Qué activos están habilitados en el catálogo y con qué APY?',
                      '¿Cuáles son los decimales y límites configurados por moneda?',
                      'Resumime los saldos y movimientos por activo este mes',
                  ];
            return {
                title,
                baseTitle: 'Activos & Comisiones',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo detallarte el catálogo de activos activos, tasas de rendimiento APY y reglas de comisiones.`,
                quickPrompts: subPrompts,
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/credit')) {
            const title = withSubTab('Créditos & TyC Legal');
            const subPrompts = subLower.includes('tyc') || subLower.includes('legal') || subLower.includes('término')
                ? [
                      '¿Qué documentos legales y advertencias CNV Art. 36 están vigentes?',
                      '¿Cómo se registra la aceptación inmutable de términos por usuario?',
                      'Ver estado de la cartera de créditos corporativos',
                  ]
                : [
                      '¿Cuántos préstamos corporativos hay activos y cuál es su estado?',
                      '¿Cómo evalúa el motor crediticio el score BCRA y el cupo?',
                      'Simular o auditar elegibilidad crediticia de un legajo',
                  ];
            return {
                title,
                baseTitle: 'Créditos & TyC Legal',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo analizar la cartera de préstamos activos, las reglas del motor de scoring BCRA y la vigencia de términos legales.`,
                quickPrompts: subPrompts,
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/usuarios') || p.includes('/users') || p.includes('/legajo')) {
            const title = withSubTab('Usuarios & Tenants (CRUD)');
            return {
                title,
                baseTitle: 'Usuarios & Tenants (CRUD)',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo informarte cuántos usuarios registrados y con KYC aprobado tenemos por tenant, o ayudarte a destrabar datos faltantes de un legajo.`,
                quickPrompts: [
                    '¿Cuántos usuarios registrados y con KYC aprobado tenemos?',
                    '¿Qué datos requiere un legajo para habilitar su línea de crédito?',
                    'Ver distribución de usuarios y actividad en los tenants activos',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/token')) {
            const title = withSubTab('Consola Token FTK');
            return {
                title,
                baseTitle: 'Consola Token FTK',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo ayudarte a consultar el estado de activos tokenizados, redes habilitadas y trazabilidad on-chain.`,
                quickPrompts: [
                    'Resumime los activos digitales configurados y sus cotizaciones',
                    '¿Cómo se validan las transacciones on-chain antes de acreditar?',
                    'Ver actividad reciente en criptoactivos de este mes',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/compliance/third-parties')) {
            const title = withSubTab('Tercerización (Sec. 10 BCRA)');
            return {
                title,
                baseTitle: 'Tercerización (Sec. 10 BCRA)',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo ayudarte a auditar el registro de proveedores críticos descentralizados, contingencia operativa y cumplimiento BCRA.`,
                quickPrompts: [
                    '¿Qué proveedores críticos están activos en la plataforma?',
                    '¿Cómo opera el aislamiento criptográfico de credenciales por tenant?',
                    'Verificar estado de resiliencia operativa y webhooks',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/compliance/fraud')) {
            const title = withSubTab('Fraude & Score BCRA');
            return {
                title,
                baseTitle: 'Fraude & Score BCRA',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo analizar las alertas de monitoreo transaccional, reglas antifraude y evaluaciones de riesgo crediticio BCRA.`,
                quickPrompts: [
                    '¿Cuántas alertas de cumplimiento y fraude hay abiertas hoy?',
                    '¿Cómo impacta la situación en Central de Deudores BCRA en el cupo?',
                    'Revisar últimos movimientos y contrapartes frecuentes',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/compliance/reports')) {
            const title = withSubTab('Regímenes BCRA (R.I.)');
            return {
                title,
                baseTitle: 'Regímenes BCRA (R.I.)',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy situado en ${title}. Puedo asistirte con los Regímenes Informativos BCRA/CNV, trazabilidad de reclamos y auditoría de riesgos operacionales.`,
                quickPrompts: [
                    'Resumime el estado de reclamos CNV y alertas regulatorias del período',
                    '¿El Libro Mayor cumple suma cero exacta para el reporte contable?',
                    'Comparar volumen de operaciones de este mes vs. el mes pasado',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/compliance')) {
            const title = withSubTab('Torre RegTech CNV/UIF');
            const subPrompts = subLower.includes('travel')
                ? [
                      '¿Cómo se valida la Travel Rule en transferencias de activos virtuales?',
                      'Resumime las transferencias externas e internas de este mes',
                      '¿Qué controles preventivos aplican sobre direcciones en agenda blanca?',
                  ]
                : subLower.includes('alert')
                ? [
                      '¿Cuántas alertas UIF/AML hay abiertas y cuántas son críticas?',
                      '¿Cuáles son los topes diarios y mensuales UIF por nivel KYC?',
                      'Analizar contrapartes y movimientos recientes',
                  ]
                : subLower.includes('matriz') || subLower.includes('risk')
                ? [
                      '¿Cómo se pondera la Matriz de Riesgo EBR por tenant?',
                      '¿Qué factores activan una alerta de riesgo elevado?',
                      'Ver estado general de cumplimiento en los tenants activos',
                  ]
                : subLower.includes('psav') || subLower.includes('gobernanza')
                ? [
                      '¿Cuál es el estado de cumplimiento PSAV (CNV RG 1058/25) por tenant?',
                      'Verificar segregación patrimonial y sellado inmutable SHA-256',
                      '¿Cuántos tenants tienen su configuración regulatoria al día?',
                  ]
                : [
                      '¿Cuántos reclamos CNV y alertas de cumplimiento están abiertos hoy?',
                      'Resumime el estado regulatorio UIF/KYC y límites vigentes',
                      'Verificar integridad del libro inmutable y segregación patrimonial',
                  ];
            return {
                title,
                baseTitle: 'Torre RegTech CNV/UIF',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo en vivo ${title}. Puedo informarte sobre reclamos CNV, alertas UIF, Travel Rule, Matriz de Riesgo EBR y gobernanza PSAV.`,
                quickPrompts: subPrompts,
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/ledger')) {
            const title = withSubTab('Partida Doble & Solvencia');
            const subPrompts = subLower.includes('snapshot') || subLower.includes('stock')
                ? [
                      '¿Cuál es el estado de los Stock Snapshots y Proof of Reserves?',
                      'Verificar que la suma neta del Ledger sea exactamente 0.00',
                      'Resumime los saldos auditados por moneda',
                  ]
                : subLower.includes('barrido') || subLower.includes('sweep')
                ? [
                      '¿Qué barridos físicos de conciliación se registraron recientemente?',
                      '¿Cómo se asegura el control de llaves en barridos custodiales?',
                      'Ver balance entre cuentas principales y cuentas de orden',
                  ]
                : [
                      '¿El Libro Mayor cumple suma cero exacta (Zero-Sum) en vivo?',
                      'Mostrame los últimos asientos reales registrados en Partida Doble',
                      'Comparar cantidad de operaciones de este mes vs. el mes pasado',
                  ];
            return {
                title,
                baseTitle: 'Partida Doble & Solvencia',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo verificar en vivo la ecuación de suma cero (∑ Debe - ∑ Haber = 0), los asientos contables inmutables, snapshots y barridos físicos.`,
                quickPrompts: subPrompts,
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/observability') || p.includes('/auditoria') || p.includes('/audit')) {
            const title = withSubTab('Observabilidad & DLQ');
            const subPrompts = subLower.includes('cuarentena') || subLower.includes('quarantine')
                ? [
                      '¿Existen asientos en cuarentena pendientes de resolución?',
                      '¿Por qué motivo entra una operación a cuarentena preventiva?',
                      'Verificar salud e integridad del Libro Mayor',
                  ]
                : subLower.includes('audit') || subLower.includes('bitácora')
                ? [
                      'Resumime los últimos eventos registrados en la bitácora de auditoría',
                      '¿Qué operadores iniciaron sesión o ejecutaron cambios recientemente?',
                      'Ver estado de alertas críticas y seguridad',
                  ]
                : [
                      '¿Hay webhooks o eventos fallidos en la Dead Letter Queue (DLQ)?',
                      'Resumime el estado de salud de los nodos, oráculos y workers',
                      'Ver últimos eventos de auditoría del Backoffice',
                  ];
            return {
                title,
                baseTitle: 'Observabilidad & DLQ',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo reportarte en tiempo real si existen eventos en la Dead Letter Queue (DLQ), asientos en cuarentena o registros de auditoría.`,
                quickPrompts: subPrompts,
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/security')) {
            const title = withSubTab('Operadores & 2FA (MFA)');
            const subPrompts = subLower.includes('dual') || subLower.includes('4-eyes') || subLower.includes('mesa')
                ? [
                      '¿Hay solicitudes pendientes en la Mesa de Control Dual (4-Eyes)?',
                      '¿Qué operaciones críticas exigen aprobación de dos SuperAdmins?',
                      'Ver listado de operadores activos y su nivel de seguridad',
                  ]
                : [
                      '¿Cuántos operadores Master tienen 2FA TOTP o Passkey activo?',
                      '¿Qué roles RBAC están configurados en el Backoffice?',
                      'Explicame cómo funciona la política de gracia MFA y bloqueo',
                  ];
            return {
                title,
                baseTitle: 'Operadores & 2FA (MFA)',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MASTER',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo informarte cuántos operadores tienen 2FA activo, los permisos por rol RBAC y el estado de la Mesa de Control Dual.`,
                quickPrompts: subPrompts,
                contextKey: `${p}::${subLower}`,
            };
        }

        const title = withSubTab('Panel General Master');
        return {
            title,
            baseTitle: 'Panel General Master',
            activeSubTab,
            phrase: `desde el ${title}`,
            portalRole: 'MASTER',
            welcomeText: `¡Hola! Soy ${copilotName} ✨, tu copiloto ejecutivo en el ${title}. Tengo acceso en tiempo real a los tenants activos, usuarios, estado de suma cero del Ledger, alertas de cumplimiento y pasarelas.`,
            quickPrompts: [
                '¿Cuántos tenants tenemos activos y cuál es su estado?',
                'Resumime la salud del Ledger, usuarios KYC y alertas abiertas',
                'Comparame el volumen de operaciones de este mes vs. el mes pasado',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    // =========================================================================
    // 2. PORTAL COMERCIO B2B (/merchant/*)
    // =========================================================================
    if (p.startsWith('/merchant')) {
        if (p.includes('/pos')) {
            const title = withSubTab('Terminal POS Comercio');
            return {
                title,
                baseTitle: 'Terminal POS Comercio',
                activeSubTab,
                phrase: `desde la ${title}`,
                portalRole: 'MERCHANT',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy acompañándote en la ${title}. Puedo ayudarte a conciliar cobros QR/Tarjeta, verificar acreditaciones inmediatas y revisar tus ventas del día.`,
                quickPrompts: [
                    'Resumime los cobros y ventas acreditados este mes',
                    '¿Qué medios de pago y rieles tengo activos en mi Terminal POS?',
                    'Comparar mis ingresos recibidos de este mes vs. el mes pasado',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/payments') || p.includes('/pagos')) {
            const title = withSubTab('Pago a Proveedores B2B');
            return {
                title,
                baseTitle: 'Pago a Proveedores B2B',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MERCHANT',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Podés subir una factura PDF o foto de los datos bancarios de tu proveedor para que extraiga el CBU/Alias y el monto y deje el pago listo.`,
                quickPrompts: [
                    'Subir captura o factura PDF de proveedor para preparar el pago',
                    '📷 Escanear CBU o Alias de proveedor con la cámara',
                    '¿Cuáles son mis proveedores y destinatarios más frecuentes este mes?',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/wallets')) {
            const title = withSubTab('Billeteras & Capital Comercio');
            return {
                title,
                baseTitle: 'Billeteras & Capital Comercio',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MERCHANT',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo detallarte la liquidez disponible de tu comercio en ARS, USDT y ETH, y ayudarte a dolarizar excedentes o programar pagos.`,
                quickPrompts: [
                    '¿Cuánto saldo disponible tengo hoy en ARS, USDT y ETH?',
                    '¿Cómo automatizo el ahorro nocturno de mi recaudación en USDT?',
                    'Analizar el flujo neto de mis cuentas comerciales este mes',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/settlements') || p.includes('/reports') || p.includes('/history')) {
            const title = withSubTab('Liquidaciones y Reportes B2B');
            return {
                title,
                baseTitle: 'Liquidaciones y Reportes B2B',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MERCHANT',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo armarte en el acto la comparativa de cobros, pagos a proveedores y flujo neto entre este mes y el mes pasado.`,
                quickPrompts: [
                    'Armame el reporte comparativo de este mes vs. el mes pasado',
                    '¿Cuántas transferencias envié y cuántos cobros recibí este mes?',
                    '¿Qué recomendaciones me das para optimizar mi tesorería comercial?',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        if (p.includes('/credit')) {
            const title = withSubTab('Crédito Comercial B2B');
            return {
                title,
                baseTitle: 'Crédito Comercial B2B',
                activeSubTab,
                phrase: `desde ${title}`,
                portalRole: 'MERCHANT',
                welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo evaluar en vivo tu cupo preaprobado de capital de trabajo y simular planes de cuotas fijas.`,
                quickPrompts: [
                    '¿Cuánto capital de trabajo tengo preaprobado hoy y en qué cuotas?',
                    'Simulame una línea de crédito comercial en 6 y 12 cuotas',
                    '¿Qué datos de mi legajo están verificados y cuáles faltan?',
                ],
                contextKey: `${p}::${subLower}`,
            };
        }

        const title = withSubTab('Portal Comercio');
        return {
            title,
            baseTitle: 'Portal Comercio',
            activeSubTab,
            phrase: `desde el ${title}`,
            portalRole: 'MERCHANT',
            welcomeText: `¡Hola! Soy ${copilotName} ✨, tu copiloto comercial en el ${title}. Puedo analizar tus ventas y liquidaciones, preparar pagos a proveedores leyendo facturas PDF o evaluar tu línea de capital de trabajo.`,
            quickPrompts: [
                'Resumime mis saldos comerciales y movimientos de este mes',
                'Subir factura PDF de proveedor para preparar el pago',
                '¿Cuánto capital de trabajo me pueden prestar hoy?',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    // =========================================================================
    // 3. PORTAL BILLETERA (/wallet/*)
    // =========================================================================
    const assetMatch = p.match(/\/assets?\/([a-z0-9_-]+)/i);
    if (assetMatch?.[1]) {
        const sym = assetMatch[1].toUpperCase();
        const disp =
            sym === 'USDT' || sym === 'TETHER'
                ? 'Tether (USDT)'
                : sym === 'ETH' || sym === 'ETHEREUM'
                ? 'Ethereum (ETH)'
                : sym === 'USDC'
                ? 'USD Coin (USDC)'
                : sym === 'BTC' || sym === 'BITCOIN'
                ? 'Bitcoin (BTC)'
                : sym;
        const title = withSubTab(`Tu Activo ${disp}`);
        return {
            title,
            baseTitle: `Tu Activo ${disp}`,
            activeSubTab,
            phrase: `desde tu activo ${disp}`,
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo tu posición en ${disp}. Puedo mostrarte tu saldo exacto, su valuación en dólares y pesos hoy, tus movimientos del mes o ayudarte a transferir y convertir.`,
            quickPrompts: [
                `¿Cuánto tengo disponible de ${sym} y cuántos movimientos hice este mes?`,
                `¿Cómo convierto mis ${sym} a pesos en el acto para pagar?`,
                '📷 Escanear Alias o datos bancarios con la cámara',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    const fiatMatch = p.match(/\/fiat\/([a-z0-9_-]+)/i);
    if (fiatMatch?.[1]) {
        const curr = fiatMatch[1].toUpperCase();
        const base =
            curr === 'ARS'
                ? 'Tu Cuenta en Pesos (ARS)'
                : curr === 'USD'
                ? 'Tu Cuenta en Dólares (USD)'
                : `Tu Cuenta ${curr}`;
        const title = withSubTab(base);
        return {
            title,
            baseTitle: base,
            activeSubTab,
            phrase: `desde ${base}`,
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo resumirte tus ingresos y envíos en ${curr} de este mes frente al mes pasado, o pre-cargar una transferencia desde una captura o PDF.`,
            quickPrompts: [
                `Resumime mi saldo y movimientos en ${curr} de este mes vs. el mes pasado`,
                '📷 Escanear Alias o CBU con la cámara para transferir',
                'Subir captura o factura PDF de pago',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/profile/agent-max') || p.includes('agent-max')) {
        const title = withSubTab('Los Fondos de Max');
        return {
            title,
            baseTitle: 'Los Fondos de Max',
            activeSubTab,
            phrase: 'desde los fondos de Max',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estás en mi panel de fondos separados y reglas automáticas. Desde acá puedo ayudarte a definir topes por operación, ahorro nocturno en USDT y alertas de autorización en tu celular.`,
            quickPrompts: [
                '¿Cuánto saldo tengo separado hoy en los fondos de Max?',
                'Recomendame qué tope diario y por operación configurar',
                '¿Cómo activo el ahorro nocturno automático en USDT?',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/approvals')) {
        const title = withSubTab('Autorizaciones de Max');
        return {
            title,
            baseTitle: 'Autorizaciones de Max',
            activeSubTab,
            phrase: 'desde las autorizaciones de Max',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo tus autorizaciones pendientes. Toda operación que supere tu tope automático queda retenida con seguridad hasta que la apruebes o rechaces acá.`,
            quickPrompts: [
                'Explicame qué operación está pendiente de mi autorización',
                '¿Si rechazo la operación los fondos vuelven al instante a mi cuenta?',
                '¿Cómo ajusto mi tope para futuras operaciones?',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/loans') || p.includes('/credit')) {
        const title = withSubTab('Gestión de Créditos');
        const subPrompts = subLower.includes('lombard') || subLower.includes('garant') || subLower.includes('cripto')
            ? [
                  '¿Cómo funciona el adelanto inmediato con garantía en USDT o ETH?',
                  '¿Cuánto puedo solicitar hoy dejando mis USDT en garantía?',
                  'Evaluar también mi línea personal en pesos',
              ]
            : [
                  '¿Cuánto tengo preaprobado hoy y en qué cuotas fijas?',
                  'Simulame $100.000 ARS en 3, 6 y 12 cuotas',
                  '¿Qué datos de mi legajo están verificados para mi línea de crédito?',
              ];
        return {
            title,
            baseTitle: 'Gestión de Créditos',
            activeSubTab,
            phrase: `desde ${title}`,
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo verificar tu legajo en el acto, consultar tu margen preaprobado en vivo o ayudarte a destrabar cualquier dato faltante acá mismo en el chat.`,
            quickPrompts: subPrompts,
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/market/swap') || p.includes('/swap') || p.includes('/exchange')) {
        const title = withSubTab('Conversión de Moneda');
        return {
            title,
            baseTitle: 'Conversión de Moneda',
            activeSubTab,
            phrase: 'desde conversión de moneda',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo informarte la cotización exacta en vivo para convertir entre Pesos (ARS), USDT y ETH, o ejecutar un pago con conversión instantánea.`,
            quickPrompts: [
                '¿A qué cotización convierto mis USDT a pesos hoy?',
                '¿Cuánto tengo disponible en ARS, USDT y ETH para convertir?',
                '¿Puedo pagar a un Alias en pesos descontando mis USDT en el acto?',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/market')) {
        const title = withSubTab('El Mercado');
        return {
            title,
            baseTitle: 'El Mercado',
            activeSubTab,
            phrase: 'desde el mercado',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title} con vos. Puedo analizar las cotizaciones en tiempo real de USDT, ETH y USD/ARS y cómo impactan en la valuación de tu portafolio.`,
            quickPrompts: [
                '¿Cómo están cotizando hoy el dólar cripto (USDT) y Ethereum (ETH)?',
                '¿Cuál es la valuación total de mi portafolio en dólares y en pesos?',
                '¿Cómo convierto USDT a pesos desde acá?',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/scan') || p.includes('/qr')) {
        const title = withSubTab('Escáner QR y Pagos');
        return {
            title,
            baseTitle: 'Escáner QR y Pagos',
            activeSubTab,
            phrase: 'desde el escáner QR',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estás en ${title}. Podés usar la cámara o subir una captura/PDF con un QR, Alias o CBU para que prepare el pago automáticamente.`,
            quickPrompts: [
                '📷 Escanear Alias o datos bancarios con la cámara',
                'Subir captura de pantalla o factura PDF para pagar',
                '¿Puedo pagar un QR en pesos usando mis USDT automáticamente?',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/transfer') || p.includes('/send') || p.includes('/pay')) {
        const title = withSubTab('Transferencias y Pagos');
        return {
            title,
            baseTitle: 'Transferencias y Pagos',
            activeSubTab,
            phrase: 'desde transferencias y pagos',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy acompañándote en ${title}. Si tenés una captura de WhatsApp, boleta PDF o foto con un Alias/CBU, subila acá y te dejo la transferencia lista para confirmar en 1 toque.`,
            quickPrompts: [
                '📷 Escanear Alias o datos bancarios con la cámara',
                'Subir captura de Alias o factura PDF para preparar transferencia',
                '¿Cuáles son mis destinatarios más frecuentes de este mes?',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/deposit') || p.includes('/receive')) {
        const title = withSubTab('Ingresar Dinero');
        return {
            title,
            baseTitle: 'Ingresar Dinero',
            activeSubTab,
            phrase: 'desde ingresar dinero',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estás en ${title}. Puedo indicarte cómo recibir pesos por transferencia inmediata o por qué red depositar USDT y ETH de forma segura.`,
            quickPrompts: [
                '¿Cuántos ingresos y depósitos recibí este mes en mis cuentas?',
                '¿Qué redes están habilitadas para depositar USDT y ETH sin errores?',
                'Resumime mis saldos actuales por moneda',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/earn') || p.includes('/staking')) {
        const title = withSubTab('Rendimientos e Inversión');
        return {
            title,
            baseTitle: 'Rendimientos e Inversión',
            activeSubTab,
            phrase: 'desde tus inversiones',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo calcular cuánto rendiría tu saldo disponible según las tasas APY vigentes y cómo activar rendimientos sin perder liquidez.`,
            quickPrompts: [
                '¿Qué tasa APY rinden mis activos y cuánto ganaría por mes?',
                '¿Tengo saldo inactivo que me convenga poner a rendir hoy?',
                '¿Puedo retirar o usar mi dinero en cualquier momento?',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/subscriptions')) {
        const title = withSubTab('Tus Suscripciones');
        return {
            title,
            baseTitle: 'Tus Suscripciones',
            activeSubTab,
            phrase: 'desde tus suscripciones',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo mostrarte tus débitos periódicos activos, ayudarte a aplicar un Stop Debit inmediato o proteger tus vencimientos.`,
            quickPrompts: [
                '¿Qué mandatos y suscripciones tengo activos hoy?',
                '¿Cómo funciona la baja o Stop Debit inmediato de un débito?',
                '¿Cómo protejo mis vencimientos con los fondos de Max?',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/history') || p.includes('/movements') || p.includes('/tx') || p.includes('/receipt')) {
        const title = withSubTab('Tus Movimientos');
        return {
            title,
            baseTitle: 'Tus Movimientos',
            activeSubTab,
            phrase: 'desde tus movimientos',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo compararte cuántas transferencias e ingresos hiciste este mes contra el mes pasado y quiénes son tus destinatarios más frecuentes.`,
            quickPrompts: [
                'Comparame mis movimientos de este mes contra el mes pasado',
                '¿A qué destinatarios les transferí más veces este mes?',
                'Mostrame el desglose de mis envíos e ingresos por moneda',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/notifications') || p.includes('/profile/alerts') || p.includes('/profile/notifications')) {
        const title = withSubTab('Tus Notificaciones');
        return {
            title,
            baseTitle: 'Tus Notificaciones',
            activeSubTab,
            phrase: 'desde tus notificaciones',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo resumirte las últimas alertas de seguridad, movimientos acreditados y avisos importantes de tu cuenta.`,
            quickPrompts: [
                'Resumime mis últimos movimientos y alertas financieras',
                '¿Qué notificaciones de seguridad son obligatorias en mi cuenta?',
                '¿Tengo alguna autorización pendiente en los fondos de Max?',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/profile/security')) {
        const title = withSubTab('Seguridad y Biometría');
        return {
            title,
            baseTitle: 'Seguridad y Biometría',
            activeSubTab,
            phrase: 'desde seguridad y biometría',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy en ${title}. Puedo guiarte para proteger tus transferencias con biometría (Passkeys), revisar tu agenda blanca de destinatarios y blindar tu cuenta.`,
            quickPrompts: [
                '¿Cuántas direcciones tengo agendadas en mi Agenda Blanca?',
                '¿Cómo protejo mis transferencias con huella o rostro (Passkey)?',
                'Revisar mis límites regulatorios diarios y mensuales disponibles',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/profile') || p.includes('/settings')) {
        const title = withSubTab('Tu Perfil');
        return {
            title,
            baseTitle: 'Tu Perfil',
            activeSubTab,
            phrase: 'desde tu perfil',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo verificar qué datos de tu legajo están completos, ayudarte a actualizar tu CUIT/teléfono/ingresos acá mismo o revisar tus límites UIF/KYC.`,
            quickPrompts: [
                '¿Qué datos de mi legajo están verificados y cuáles me faltan completar?',
                '¿Cuál es mi nivel KYC actual y mi margen mensual disponible?',
                'Ir a configurar los fondos y misiones de Max',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    if (p.includes('/assets') || p.includes('/billetera')) {
        const title = withSubTab('Tu Billetera');
        return {
            title,
            baseTitle: 'Tu Billetera',
            activeSubTab,
            phrase: 'desde tu billetera',
            portalRole: 'WALLET',
            welcomeText: `¡Hola! Soy ${copilotName} ✨. Estoy viendo ${title}. Puedo detallarte tus saldos en Pesos (ARS), USDT y ETH, comparar tus movimientos del mes o preparar una transferencia desde una foto o PDF.`,
            quickPrompts: [
                'Resumime todos mis saldos disponibles y su valuación en USD y ARS',
                'Comparame mis transferencias de este mes vs. el mes pasado',
                '📷 Escanear Alias o datos bancarios con la cámara',
            ],
            contextKey: `${p}::${subLower}`,
        };
    }

    const title = withSubTab('Tu Inicio');
    return {
        title,
        baseTitle: 'Tu Inicio',
        activeSubTab,
        phrase: 'desde tu inicio',
        portalRole: 'WALLET',
        welcomeText: `¡Hola! Soy ${copilotName} ✨, tu copiloto financiero inteligente. Estoy viendo el contexto ${
            activeSubTab ? `de ${title}` : 'desde tu inicio'
        }. Podés consultarme tus saldos y reportes del mes, escanear un Alias o factura PDF para transferir, o simular tu línea de crédito en vivo.`,
        quickPrompts: [
            '¿Cuánto tengo disponible en mis cuentas y cuánto moví este mes?',
            '📷 Escanear Alias o datos bancarios con la cámara',
            '¿Cuánto me pueden prestar hoy y en qué cuotas?',
        ],
        contextKey: `${p}::${subLower}`,
    };
}

function buildInitialWelcomeTurn(copilotName: string, screenMeta: ScreenContextMeta): CopilotTurnMessage {
    return {
        id: `welcome-${screenMeta.contextKey}-${Date.now()}`,
        role: 'assistant',
        text: screenMeta.welcomeText,
        timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
        suggestedFollowUps: screenMeta.quickPrompts,
    };
}

export default function MaxFloatingCopilot() {
    const pathname = usePathname() || '/wallet';
    const router = useRouter();
    const { corporateId, config } = useCorporate();

    const aiTheme = (config?.ui_theme as Record<string, any>)?.ai_agents || {};
    const copilotName = String(aiTheme.copilot_name || 'Max');

    const [isOpen, setIsOpen] = useState(false);
    const [showHistoryView, setShowHistoryView] = useState(false);
    const [shareScreenContext, setShareScreenContext] = useState(true);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isExecutingPayment, setIsExecutingPayment] = useState(false);
    const [voiceOutputEnabled, setVoiceOutputEnabled] = useState(true);
    const [isListeningMic, setIsListeningMic] = useState(false);
    const [attachedImageBase64, setAttachedImageBase64] = useState<string | null>(null);
    const [attachedImageMime, setAttachedImageMime] = useState<string>('image/png');
    const [attachedImagePreview, setAttachedImagePreview] = useState<string | null>(null);
    const [attachedFileName, setAttachedFileName] = useState<string | null>(null);
    const [domSnapshot, setDomSnapshot] = useState<LiveDomScreenSnapshot>({
        activeSubTab: '',
        pageHeading: '',
        sectionHeading: '',
        metricsSummary: '',
    });

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const cameraInputRef = useRef<HTMLInputElement | null>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const recognitionRef = useRef<any>(null);

    const screenMeta = resolveScreenTitle(pathname, domSnapshot.activeSubTab, copilotName);
    const storageKey = `max_copilot_sessions_v2_${corporateId || 'default'}`;

    const [sessions, setSessions] = useState<CopilotChatSession[]>([]);
    const [inlineBlockerInput, setInlineBlockerInput] = useState<string>('');
    const [activeSessionId, setActiveSessionId] = useState<string>(() => `chat-${Date.now()}`);
    const [messages, setMessages] = useState<CopilotTurnMessage[]>(() => [
        buildInitialWelcomeTurn(copilotName, screenMeta),
    ]);

    // Observador reactivo del DOM para detectar cambios de pestaña interna (activeTab) y métricas en pantalla en tiempo real
    useEffect(() => {
        if (typeof window === 'undefined') return;
        let timerId: ReturnType<typeof setTimeout> | null = null;

        const syncDomContext = () => {
            const snap = extractLiveDomScreenSnapshot();
            setDomSnapshot((prev) => {
                if (
                    prev.activeSubTab === snap.activeSubTab &&
                    prev.pageHeading === snap.pageHeading &&
                    prev.sectionHeading === snap.sectionHeading &&
                    prev.metricsSummary === snap.metricsSummary
                ) {
                    return prev;
                }
                return snap;
            });
        };

        const scheduleSync = () => {
            if (timerId) clearTimeout(timerId);
            timerId = setTimeout(syncDomContext, 60);
        };

        syncDomContext();
        const initialTimer = setTimeout(syncDomContext, 220);

        window.addEventListener('click', scheduleSync, true);
        window.addEventListener('popstate', scheduleSync);

        const targetNode = document.querySelector('main') || document.body;
        const observer = new MutationObserver(scheduleSync);
        observer.observe(targetNode, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'aria-selected', 'data-state'],
        });

        return () => {
            if (timerId) clearTimeout(timerId);
            clearTimeout(initialTimer);
            window.removeEventListener('click', scheduleSync, true);
            window.removeEventListener('popstate', scheduleSync);
            observer.disconnect();
        };
    }, [pathname, isOpen]);

    // Actualizar instantáneamente el turno de bienvenida y sugerencias cuando el usuario cambia de ruta o pestaña interna
    useEffect(() => {
        setShareScreenContext(true);
        const latestMeta = resolveScreenTitle(pathname, domSnapshot.activeSubTab, copilotName);
        setMessages((prev) => {
            const hasUserInteraction = prev.some((m) => m.role === 'user');
            if (!hasUserInteraction) {
                return [buildInitialWelcomeTurn(copilotName, latestMeta)];
            }
            return prev;
        });
    }, [screenMeta.contextKey, pathname, domSnapshot.activeSubTab, copilotName]);

    // Cargar historial de conversaciones previas sin pisar el mensaje contextual de la pestaña activa
    useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const raw = window.localStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw) as CopilotChatSession[];
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setSessions(parsed);
                }
            }
        } catch {
            // Ignorar si localStorage está restringido
        }
    }, [storageKey]);

    const persistSessionMessages = useCallback(
        (sessionId: string, updatedMessages: CopilotTurnMessage[]) => {
            const firstUserMsg = updatedMessages.find((m) => m.role === 'user');
            const computedTitle = firstUserMsg
                ? firstUserMsg.text.replace(/^[📷📄]\s*/, '').slice(0, 46)
                : `Consulta en ${screenMeta.title}`;
            const nowStr = new Date().toLocaleString('es-AR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            });

            setSessions((prev) => {
                const filtered = prev.filter((s) => s.id !== sessionId);
                const nextSession: CopilotChatSession = {
                    id: sessionId,
                    title: computedTitle,
                    updatedAt: nowStr,
                    screenLabel: screenMeta.title,
                    messages: updatedMessages,
                };
                const nextList = [nextSession, ...filtered].slice(0, 25);
                if (typeof window !== 'undefined') {
                    try {
                        window.localStorage.setItem(storageKey, JSON.stringify(nextList));
                    } catch {
                        // ignore
                    }
                }
                return nextList;
            });
        },
        [screenMeta.title, storageKey]
    );

    const handleStartNewChat = () => {
        const newId = `chat-${Date.now()}`;
        const welcomeTurn = buildInitialWelcomeTurn(copilotName, screenMeta);
        setActiveSessionId(newId);
        setMessages([welcomeTurn]);
        setShowHistoryView(false);
        setShareScreenContext(true);
        setInputMessage('');
        setAttachedImageBase64(null);
        setAttachedImagePreview(null);
        setAttachedFileName(null);
    };

    const handleSelectSession = (session: CopilotChatSession) => {
        setActiveSessionId(session.id);
        setMessages(session.messages);
        setShowHistoryView(false);
    };

    const handleDeleteSession = (sessionId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const nextList = sessions.filter((s) => s.id !== sessionId);
        setSessions(nextList);
        if (typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(storageKey, JSON.stringify(nextList));
            } catch {
                // ignore
            }
        }
        if (activeSessionId === sessionId) {
            if (nextList.length > 0) {
                setActiveSessionId(nextList[0].id);
                setMessages(nextList[0].messages);
            } else {
                handleStartNewChat();
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen]);

    const speakOutLoud = useCallback((textToSpeak: string) => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
        try {
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(textToSpeak);
            utterance.lang = 'es-AR';
            utterance.rate = 1.04;
            window.speechSynthesis.speak(utterance);
        } catch {
            // Ignorar si el navegador bloquea síntesis sin gesto previo
        }
    }, []);

    const handleImageFileSelect = (file: File) => {
        const isPdf =
            file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        const isImage = file.type.startsWith('image/');

        if (!isImage && !isPdf) {
            toast.error('Por favor seleccioná una imagen (PNG, JPG, WebP) o un documento PDF (.pdf).');
            return;
        }
        const resolvedMime = isPdf ? 'application/pdf' : file.type || 'image/png';
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result || '');
            const base64Part = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
            setAttachedImageBase64(base64Part);
            setAttachedImageMime(resolvedMime);
            setAttachedImagePreview(dataUrl);
            setAttachedFileName(file.name || (isPdf ? 'documento.pdf' : 'captura.png'));
            toast.success(
                isPdf
                    ? 'Documento PDF cargado. Enviá tu mensaje para que Max lo lea.'
                    : 'Captura cargada. Enviá tu mensaje para que Max la analice.'
            );
        };
        reader.readAsDataURL(file);
    };

    const handlePasteClipboard = (e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
            const itemType = items[i].type || '';
            if (itemType.startsWith('image/') || itemType === 'application/pdf') {
                const file = items[i].getAsFile();
                if (file) {
                    handleImageFileSelect(file);
                    e.preventDefault();
                    break;
                }
            }
        }
    };

    const toggleMicrophone = () => {
        if (typeof window === 'undefined') return;
        const SpeechRecognitionCtor =
            (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

        if (!SpeechRecognitionCtor) {
            toast.error('Tu navegador no soporta dictado por voz directo.');
            return;
        }

        if (isListeningMic && recognitionRef.current) {
            recognitionRef.current.stop();
            setIsListeningMic(false);
            return;
        }

        const recognition = new SpeechRecognitionCtor();
        recognition.lang = 'es-AR';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => setIsListeningMic(true);
        recognition.onend = () => setIsListeningMic(false);
        recognition.onerror = () => setIsListeningMic(false);
        recognition.onresult = (event: any) => {
            const transcript = event.results?.[0]?.[0]?.transcript || '';
            if (transcript) {
                setInputMessage((prev) => (prev ? `${prev} ${transcript}` : transcript));
            }
        };

        recognitionRef.current = recognition;
        recognition.start();
    };

    const sendTurnToCopilot = async (overrideMessage?: string) => {
        const rawText = (overrideMessage !== undefined ? overrideMessage : inputMessage).trim();
        if (!rawText && !attachedImageBase64) return;

        const userMsgId = `usr-${Date.now()}`;
        const currentPreview = attachedImagePreview;
        const currentBase64 = attachedImageBase64;
        const currentMime = attachedImageMime;
        const currentFileName = attachedFileName;

        const newUserTurn: CopilotTurnMessage = {
            id: userMsgId,
            role: 'user',
            text:
                rawText ||
                (currentMime === 'application/pdf'
                    ? `📄 Analizá este documento PDF (${currentFileName || 'factura.pdf'}) y prepará la operación.`
                    : '📷 Analizá esta captura de pantalla y prepará la operación.'),
            timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
            imagePreviewUrl: currentPreview || undefined,
            attachmentMimeType: currentMime || undefined,
            attachmentFileName: currentFileName || undefined,
        };

        const messagesWithUser = [...messages, newUserTurn];
        setMessages(messagesWithUser);
        persistSessionMessages(activeSessionId, messagesWithUser);
        setInputMessage('');
        setAttachedImageBase64(null);
        setAttachedImagePreview(null);
        setAttachedFileName(null);
        setIsLoading(true);

        try {
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (corporateId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(corporateId)) {
                headers['x-corporate-id'] = corporateId;
            }

            const historyPayload = messages.slice(-6).map((m) => ({
                role: m.role,
                text: m.text,
            }));

            const res = await fetch('/api/wallet/agent/copilot', {
                method: 'POST',
                credentials: 'include',
                headers,
                body: JSON.stringify({
                    message: rawText,
                    screenContext: {
                        pathname: shareScreenContext ? pathname : '/wallet',
                        screenTitle: shareScreenContext ? screenMeta.title : 'Tu Billetera',
                        portalRole: screenMeta.portalRole,
                        visibleSummary: shareScreenContext ? domSnapshot.metricsSummary : undefined,
                        visibleDataSummary: {
                            activeScreenLabel: screenMeta.title,
                            activeSubTab: screenMeta.activeSubTab,
                            pageHeading: domSnapshot.pageHeading,
                            sectionHeading: domSnapshot.sectionHeading,
                            metricsSummary: domSnapshot.metricsSummary,
                        },
                    },
                    imageBase64: currentBase64 || undefined,
                    imageMimeType: currentBase64 ? currentMime : undefined,
                    conversationHistory: historyPayload,
                }),
            });

            const json = await res.json();
            if (!res.ok || !json.success) {
                throw new Error(json.error?.message || json.error || 'No se pudo consultar a Max.');
            }

            const data = json.data;
            const assistantMsg: CopilotTurnMessage = {
                id: `max-${Date.now()}`,
                role: 'assistant',
                text: String(data.replyText || '').replace(/\*\*/g, ''),
                spokenText: data.spokenSummaryText,
                timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
                intentCategory: data.intentCategory,
                disambiguationQuestions: data.disambiguation?.missingFields || [],
                suggestedFollowUps:
                    Array.isArray(data.suggestedFollowUps) && data.suggestedFollowUps.length > 0
                        ? data.suggestedFollowUps
                        : screenMeta.quickPrompts,
                loanSimulationCard: data.loanSimulationCard || null,
                preparedPaymentCard: data.preparedPaymentCard || null,
                financialReportCard: data.financialReportCard || null,
            };

            const finalMessages = [...messagesWithUser, assistantMsg];
            setMessages(finalMessages);
            persistSessionMessages(activeSessionId, finalMessages);

            if (voiceOutputEnabled && data.spokenSummaryText) {
                speakOutLoud(data.spokenSummaryText);
            }
        } catch (err: any) {
            toast.error(err?.message || 'Error al comunicarse con Max');
            const errTurn: CopilotTurnMessage = {
                id: `err-${Date.now()}`,
                role: 'assistant',
                text: `No pude procesar la solicitud en este momento: ${err?.message || 'Verificá tu sesión activa.'}`,
                timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
            };
            const finalErrMessages = [...messagesWithUser, errTurn];
            setMessages(finalErrMessages);
            persistSessionMessages(activeSessionId, finalErrMessages);
        } finally {
            setIsLoading(false);
        }
    };

    const handleExecutePreparedPayment = async (
        card: NonNullable<CopilotTurnMessage['preparedPaymentCard']>
    ) => {
        setIsExecutingPayment(true);
        try {
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (corporateId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(corporateId)) {
                headers['x-corporate-id'] = corporateId;
            }

            const res = await fetch('/api/wallet/agent/execute', {
                method: 'POST',
                credentials: 'include',
                headers,
                body: JSON.stringify({
                    mode: 'ASSISTED_SWAP_AND_TRANSFER',
                    targetCurrency: card.targetCurrency,
                    requiredTargetHuman: card.requiredAmountHuman,
                    swapSourceCurrency: card.swapSourceCurrency,
                    recipientLabel: `${card.recipientName} (${card.recipientAliasOrCvu})`,
                    rail: card.rail,
                    subaccountLabel: card.subaccountLabel,
                }),
            });

            const json = await res.json();
            if (!res.ok || !json.success) {
                throw new Error(json.error?.message || json.error || 'Error al ejecutar operación atómica.');
            }

            const txData = json.data;
            toast.success('¡Transferencia realizada con éxito!');

            const summaryText = txData.swapExecuted
                ? `✅ ¡Listo! Ejecuté el Auto-Swap de ${txData.swappedSourceHuman} ${txData.swapSourceCurrency} y transferí $${txData.transferredTargetHuman} ${txData.targetCurrency} a ${card.recipientName} (${card.recipientAliasOrCvu}) bajo un único identificador raíz (${String(txData.rootTransactionGroupId).slice(0, 8)}...).`
                : `✅ ¡Listo! Transferí $${txData.transferredTargetHuman} ${txData.targetCurrency} a ${card.recipientName} (${card.recipientAliasOrCvu}) con comprobante inmutable (${String(txData.rootTransactionGroupId).slice(0, 8)}...).`;

            setMessages((prev) => [
                ...prev,
                {
                    id: `exec-${Date.now()}`,
                    role: 'assistant',
                    text: summaryText,
                    spokenText: summaryText,
                    timestamp: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }),
                    suggestedFollowUps: ['Ver mis movimientos', 'Ir a Aprendé más de Max'],
                },
            ]);

            if (voiceOutputEnabled) {
                speakOutLoud(summaryText);
            }
        } catch (err: any) {
            toast.error(err?.message || 'No se pudo completar el pago asistido');
        } finally {
            setIsExecutingPayment(false);
        }
    };

    const hiddenRoutes = ['/wallet/welcome', '/wallet/onboarding', '/wallet/recover'];
    const isMerchantOrMaster =
        pathname.startsWith('/merchant') || pathname.startsWith('/master');

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleExternalPrompt = (evt: Event) => {
            const customEvt = evt as CustomEvent<{ prompt?: string; openDrawer?: boolean }>;
            if (customEvt.detail?.openDrawer !== false) {
                setIsOpen(true);
            }
            const incomingPrompt = customEvt.detail?.prompt?.trim();
            if (incomingPrompt) {
                void sendTurnToCopilot(incomingPrompt);
            }
        };
        window.addEventListener('konex:max-copilot-prompt', handleExternalPrompt as EventListener);
        return () => {
            window.removeEventListener('konex:max-copilot-prompt', handleExternalPrompt as EventListener);
        };
    });

    if (hiddenRoutes.some((r) => pathname.startsWith(r))) {
        return null;
    }

    const isWalletRoute = pathname.startsWith('/wallet');
    const NO_NAVBAR_WALLET_ROUTES = [
        '/wallet/welcome',
        '/wallet/onboarding',
        '/wallet/recover',
        '/wallet/scan',
        '/wallet/send',
        '/wallet/receive',
        '/wallet/debug',
        '/wallet/receipt',
    ];
    const hasWalletBottomNavbar =
        isWalletRoute && !NO_NAVBAR_WALLET_ROUTES.some((route) => pathname.startsWith(route));
    const walletTheme = config?.ui_theme?.wallet || config?.ui_theme || {};
    const walletColors = walletTheme?.colors || config?.ui_theme?.colors || {};
    const bgCandidate = String(walletColors.background || '').toLowerCase();
    const isLightMode =
        walletTheme?.mode === 'light' ||
        config?.ui_theme?.mode === 'light' ||
        bgCandidate === '#ffffff' ||
        bgCandidate === '#f8fafc' ||
        bgCandidate === '#f0f7ff';
    const primaryHex = walletColors.primary || config?.hexPrimary || '#136dec';

    return (
        <>
            {/* Globo Flotante Inferior Derecho 100% LiquidGlass con L&F del Tenant ✨ */}
            <div
                className={`fixed z-50 ${
                    hasWalletBottomNavbar ? 'bottom-24 right-4' : 'bottom-6 right-4'
                } flex items-center gap-2`}
            >
                <button
                    type="button"
                    onClick={() => setIsOpen((prev) => !prev)}
                    aria-label={`Abrir copiloto ${copilotName}`}
                    data-testid="max-floating-orb"
                    className={`group relative flex items-center gap-2.5 rounded-full px-4 py-2.5 backdrop-blur-2xl transition-all duration-300 hover:scale-105 active:scale-95 ${
                        isLightMode
                            ? 'bg-white/85 text-slate-900 border-[1.5px] border-white/95 shadow-[0_12px_32px_rgba(19,109,236,0.22)]'
                            : 'bg-slate-900/75 text-white border-[1.5px] border-white/30 shadow-[0_12px_32px_rgba(19,109,236,0.38)]'
                    }`}
                    style={{
                        backgroundImage: isLightMode
                            ? `linear-gradient(135deg, rgba(255,255,255,0.94) 0%, ${primaryHex}18 55%, rgba(255,255,255,0.72) 100%)`
                            : `linear-gradient(135deg, rgba(255,255,255,0.16) 0%, ${primaryHex}35 55%, rgba(15,23,42,0.82) 100%)`,
                    }}
                >
                    <span
                        className="relative flex h-8 w-8 items-center justify-center rounded-full border border-white/50 shadow-inner"
                        style={{ backgroundColor: primaryHex }}
                    >
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-25" />
                        <Sparkles className="h-4 w-4 text-white transition-transform duration-300 group-hover:rotate-12" />
                    </span>
                    <div className="flex flex-col items-start leading-none pr-1">
                        <span className="text-xs font-black tracking-tight flex items-center gap-1">
                            {copilotName} ✨
                        </span>
                        <span
                            className="text-[10px] font-extrabold mt-0.5"
                            style={{ color: primaryHex }}
                        >
                            Asistente IA
                        </span>
                    </div>
                </button>
            </div>

            {/* Drawer / Bottom Sheet Multimodal de Max 100% LiquidGlass en Zona Inferior Derecha */}
            {isOpen && (
                <div
                    data-max-copilot-drawer="true"
                    className={`fixed inset-0 z-[120] flex items-end sm:items-end justify-center sm:justify-end p-0 sm:p-6 animate-in fade-in duration-200 backdrop-blur-md ${
                        isLightMode ? 'bg-slate-900/25' : 'bg-black/55'
                    }`}
                >
                    <div
                        className={`relative flex flex-col w-full max-w-md h-[86vh] sm:h-[650px] rounded-t-[32px] sm:rounded-[32px] backdrop-blur-3xl overflow-hidden transition-all ${
                            isLightMode
                                ? 'bg-[#f2f8ff]/90 text-slate-900 border-[1.5px] border-white/95 shadow-[0_24px_60px_rgba(15,23,42,0.18)]'
                                : 'bg-[#0a101c]/88 text-white border-[1.5px] border-white/25 shadow-[0_24px_60px_rgba(0,0,0,0.65)]'
                        }`}
                        style={{
                            backgroundImage: isLightMode
                                ? `radial-gradient(circle at 18% 10%, ${primaryHex}24 0%, transparent 55%), linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(240,247,255,0.78) 100%)`
                                : `radial-gradient(circle at 18% 10%, ${primaryHex}30 0%, transparent 55%), linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(10,16,28,0.88) 100%)`,
                        }}
                        onPaste={handlePasteClipboard}
                    >
                        {/* Top Specular Status Accent Pill */}
                        <div
                            className="mx-auto h-1 w-14 rounded-b-full opacity-90"
                            style={{ backgroundColor: primaryHex }}
                        />

                        {/* Header LiquidGlass Estilo Gemini: Historial, Nuevo Chat, Voz y Cerrar */}
                        <div
                            className={`px-4 py-3.5 border-b flex items-center justify-between backdrop-blur-xl ${
                                isLightMode
                                    ? 'bg-white/65 border-slate-200/80'
                                    : 'bg-white/5 border-white/15'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-10 h-10 rounded-2xl border border-white/50 flex items-center justify-center shadow-md"
                                    style={{ backgroundColor: primaryHex }}
                                >
                                    <Sparkles className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5">
                                        <h3 className="text-sm font-black tracking-tight">
                                            {copilotName} Copiloto ✨
                                        </h3>
                                        <span
                                            className="text-[9px] px-2 py-0.5 rounded-full font-extrabold border"
                                            style={{
                                                backgroundColor: `${primaryHex}18`,
                                                borderColor: `${primaryHex}40`,
                                                color: primaryHex,
                                            }}
                                        >
                                            LIQUID IA
                                        </span>
                                    </div>
                                    <p
                                        className={`text-[10px] flex items-center gap-1 mt-0.5 font-semibold ${
                                            isLightMode ? 'text-slate-600' : 'text-slate-300'
                                        }`}
                                    >
                                        <Compass
                                            className="w-3 h-3 shrink-0"
                                            style={{ color: primaryHex }}
                                        />
                                        <span className="truncate max-w-[180px]">
                                            Viendo {screenMeta.phrase.replace(/^desde /, '')}
                                        </span>
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-1.5">
                                {/* Botón Historial de Consultas (Estilo Gemini) */}
                                <button
                                    type="button"
                                    onClick={() => setShowHistoryView((prev) => !prev)}
                                    title="Historial de consultas"
                                    data-testid="max-copilot-history"
                                    className={`p-2 rounded-xl border text-xs transition-colors backdrop-blur-md ${
                                        showHistoryView
                                            ? 'border-white/50 text-white shadow-sm'
                                            : isLightMode
                                            ? 'bg-white/75 border-slate-200 text-slate-600 hover:bg-white'
                                            : 'bg-white/8 border-white/15 text-slate-300 hover:bg-white/15'
                                    }`}
                                    style={
                                        showHistoryView
                                            ? { backgroundColor: primaryHex }
                                            : undefined
                                    }
                                >
                                    <Clock className="w-4 h-4" />
                                </button>

                                {/* Botón Nuevo Chat (Estilo Gemini) */}
                                <button
                                    type="button"
                                    onClick={handleStartNewChat}
                                    title="Empezar un chat nuevo"
                                    data-testid="max-copilot-new-chat"
                                    className={`p-2 rounded-xl border text-xs transition-colors backdrop-blur-md ${
                                        isLightMode
                                            ? 'bg-white/75 border-slate-200 text-slate-600 hover:bg-white'
                                            : 'bg-white/8 border-white/15 text-slate-300 hover:bg-white/15'
                                    }`}
                                >
                                    <SquarePen className="w-4 h-4" />
                                </button>

                                {/* Botón Voz */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        const nextState = !voiceOutputEnabled;
                                        setVoiceOutputEnabled(nextState);
                                        if (!nextState && typeof window !== 'undefined' && 'speechSynthesis' in window) {
                                            window.speechSynthesis.cancel();
                                        }
                                    }}
                                    title={voiceOutputEnabled ? 'Voz de Max activada (es-AR)' : 'Voz de Max silenciada'}
                                    className={`p-2 rounded-xl border text-xs transition-colors backdrop-blur-md ${
                                        voiceOutputEnabled
                                            ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-600 dark:text-emerald-300'
                                            : isLightMode
                                            ? 'bg-white/75 border-slate-200 text-slate-600 hover:bg-white'
                                            : 'bg-white/8 border-white/15 text-slate-300'
                                    }`}
                                >
                                    {voiceOutputEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                                </button>

                                {/* Botón Cerrar */}
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className={`p-2 rounded-xl border transition-colors backdrop-blur-md ${
                                        isLightMode
                                            ? 'bg-white/75 hover:bg-white border-slate-200/90 text-slate-700'
                                            : 'bg-white/8 hover:bg-white/15 border-white/15 text-slate-200'
                                    }`}
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {showHistoryView ? (
                            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                <div className="flex items-center justify-between mb-1">
                                    <div>
                                        <h4 className="text-xs font-black tracking-tight">
                                            Historial de consultas
                                        </h4>
                                        <p
                                            className={`text-[11px] ${
                                                isLightMode ? 'text-slate-500' : 'text-slate-400'
                                            }`}
                                        >
                                            Retomá una conversación anterior o iniciá un chat nuevo
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleStartNewChat}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-extrabold text-white shadow-sm"
                                        style={{ backgroundColor: primaryHex }}
                                    >
                                        <SquarePen className="w-3.5 h-3.5" />
                                        Nuevo chat
                                    </button>
                                </div>

                                {sessions.length === 0 ? (
                                    <div
                                        className={`p-6 rounded-2xl border text-center space-y-2 ${
                                            isLightMode
                                                ? 'bg-white/75 border-slate-200 text-slate-600'
                                                : 'bg-white/5 border-white/10 text-slate-300'
                                        }`}
                                    >
                                        <MessageSquare
                                            className="w-7 h-7 mx-auto opacity-80"
                                            style={{ color: primaryHex }}
                                        />
                                        <p className="text-xs font-extrabold">
                                            Todavía no hay consultas guardadas
                                        </p>
                                        <p className="text-[11px] opacity-75">
                                            Tus conversaciones con {copilotName} aparecerán acá automáticamente.
                                        </p>
                                    </div>
                                ) : (
                                    sessions.map((sess) => {
                                        const isCurrent = sess.id === activeSessionId;
                                        return (
                                            <div
                                                key={sess.id}
                                                onClick={() => handleSelectSession(sess)}
                                                className={`cursor-pointer p-3 rounded-2xl border flex items-center justify-between gap-2 transition-all ${
                                                    isCurrent
                                                        ? isLightMode
                                                            ? 'bg-white border-blue-400 shadow-sm'
                                                            : 'bg-white/15 border-white/40'
                                                        : isLightMode
                                                        ? 'bg-white/70 hover:bg-white border-slate-200/80'
                                                        : 'bg-white/5 hover:bg-white/10 border-white/10'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    <div
                                                        className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                                                        style={{
                                                            backgroundColor: isCurrent
                                                                ? primaryHex
                                                                : `${primaryHex}20`,
                                                        }}
                                                    >
                                                        <MessageSquare
                                                            className="w-4 h-4"
                                                            style={{
                                                                color: isCurrent ? '#ffffff' : primaryHex,
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-xs font-extrabold truncate">
                                                            {sess.title}
                                                        </p>
                                                        <p
                                                            className={`text-[10px] truncate ${
                                                                isLightMode
                                                                    ? 'text-slate-500'
                                                                    : 'text-slate-400'
                                                            }`}
                                                        >
                                                            {sess.screenLabel} · {sess.updatedAt}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleDeleteSession(sess.id, e)}
                                                    title="Eliminar consulta"
                                                    className="p-1.5 rounded-lg text-rose-400 hover:bg-rose-500/15 transition-colors shrink-0"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        );
                                    })
                                )}
                                <p
                                    className={`pt-2 text-center text-[10px] font-semibold opacity-75 ${
                                        isLightMode ? 'text-slate-500' : 'text-slate-400'
                                    }`}
                                >
                                    {copilotName} es una IA y puede cometer errores.
                                </p>
                            </div>
                        ) : (
                        <>
                        {/* Cuerpo de Mensajes e Interacciones en Tarjetas LiquidGlass */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {messages.map((msg) => (
                                <div
                                    key={msg.id}
                                    className={`flex flex-col ${
                                        msg.role === 'user' ? 'items-end' : 'items-start'
                                    }`}
                                >
                                    <div
                                        className={`max-w-[88%] rounded-[22px] px-4 py-3 text-xs leading-relaxed backdrop-blur-xl transition-all ${
                                            msg.role === 'user'
                                                ? 'text-white rounded-br-md border border-white/40 shadow-md'
                                                : isLightMode
                                                ? 'bg-white/80 border-[1.2px] border-white/95 text-slate-900 rounded-bl-md shadow-[0_8px_24px_rgba(15,23,42,0.06)]'
                                                : 'bg-white/10 border-[1.2px] border-white/20 text-slate-100 rounded-bl-md shadow-lg'
                                        }`}
                                        style={
                                            msg.role === 'user'
                                                ? {
                                                      backgroundColor: primaryHex,
                                                      backgroundImage:
                                                          'linear-gradient(135deg, rgba(255,255,255,0.26) 0%, transparent 100%)',
                                                  }
                                                : undefined
                                        }
                                    >
                                        {msg.imagePreviewUrl && (
                                            <div className="mb-2 rounded-xl overflow-hidden border border-white/20">
                                                {msg.attachmentMimeType === 'application/pdf' ? (
                                                    <div className="flex items-center gap-2.5 p-2.5 bg-slate-900/80 text-cyan-200">
                                                        <FileText className="w-5 h-5 text-cyan-400 shrink-0" />
                                                        <div className="text-[11px] leading-tight">
                                                            <p className="font-bold text-white truncate max-w-[190px]">
                                                                {msg.attachmentFileName || 'Documento.pdf'}
                                                            </p>
                                                            <p className="text-[10px] text-cyan-300/80">
                                                                PDF enviado a {copilotName}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={msg.imagePreviewUrl}
                                                        alt="Captura subida a Max"
                                                        className="max-h-44 w-auto object-contain"
                                                    />
                                                )}
                                            </div>
                                        )}
                                        <p className="whitespace-pre-line">{msg.text}</p>

                                        <div className="mt-1.5 flex items-center justify-between gap-2 text-[9px] opacity-65">
                                            <span>{msg.timestamp}</span>
                                            {msg.role === 'assistant' && msg.spokenText && (
                                                <button
                                                    type="button"
                                                    onClick={() => speakOutLoud(msg.spokenText || msg.text)}
                                                    className="inline-flex items-center gap-1 text-cyan-300 hover:underline font-semibold"
                                                >
                                                    <Volume2 className="w-3 h-3" /> Escuchar
                                                </button>
                                            )}
                                        </div>
                                        {msg.role === 'assistant' && (
                                            <p
                                                className={`mt-1 text-[9.5px] font-semibold opacity-70 ${
                                                    isLightMode ? 'text-slate-500' : 'text-slate-400'
                                                }`}
                                            >
                                                {copilotName} es una IA y puede cometer errores.
                                            </p>
                                        )}
                                    </div>

                                    {/* Tarjeta Interactiva de Destrabe y Acreditación de Préstamos */}
                                    {msg.loanSimulationCard && (
                                        <div className="mt-2.5 w-full max-w-[92%] rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-950/60 to-slate-900 border border-indigo-400/30 p-3.5 shadow-xl space-y-2.5">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[11px] font-extrabold text-indigo-300 flex items-center gap-1.5">
                                                    <Calculator className="w-3.5 h-3.5" />
                                                    {msg.loanSimulationCard.eligible !== false
                                                        ? 'Tu Línea Preaprobada'
                                                        : msg.loanSimulationCard.simulationMode ===
                                                              'JABA_SCORING_REVIEW_REQUIRED' ||
                                                          msg.loanSimulationCard.jabaScoringDetail
                                                        ? '📊 Evaluación Crediticia en Vivo'
                                                        : '🔓 Completar Datos para tu Línea'}
                                                </span>
                                                <span
                                                    className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                                                        msg.loanSimulationCard.eligible === false
                                                            ? 'bg-amber-500/20 text-amber-300'
                                                            : 'bg-emerald-500/20 text-emerald-300'
                                                    }`}
                                                >
                                                    {msg.loanSimulationCard.eligible === false
                                                        ? msg.loanSimulationCard.jabaScoringDetail
                                                            ? `Límite Disp: $0,00 ARS`
                                                            : 'Recolección en Chat'
                                                        : `Límite Disp: $${
                                                              msg.loanSimulationCard.availableCreditLimitArsHuman ||
                                                              (msg.loanSimulationCard as any).availableLimitHuman ||
                                                              '0.00'
                                                          }`}
                                                </span>
                                            </div>

                                            {Array.isArray(msg.loanSimulationCard.savedFieldsInTurn) &&
                                                msg.loanSimulationCard.savedFieldsInTurn.length > 0 && (
                                                    <div className="rounded-xl bg-emerald-500/15 border border-emerald-400/40 px-2.5 py-2 space-y-1">
                                                        {msg.loanSimulationCard.savedFieldsInTurn.map((sf, idx) => (
                                                            <p
                                                                key={sf.fieldKey || sf.label || idx}
                                                                className="text-[11px] font-extrabold text-emerald-300"
                                                            >
                                                                ✅ Guardado en tu perfil: {sf.label} ({sf.value})
                                                            </p>
                                                        ))}
                                                    </div>
                                                )}

                                            {Array.isArray(msg.loanSimulationCard.verifiedFields) &&
                                                msg.loanSimulationCard.verifiedFields.length > 0 && (
                                                    <div className="space-y-1 text-[11px] text-slate-300">
                                                        {msg.loanSimulationCard.verifiedFields.map((vf, idx) => (
                                                            <p key={vf.fieldKey || vf.label || idx}>
                                                                ✓ {vf.label}:{' '}
                                                                <strong className="text-white">{vf.value}</strong>
                                                            </p>
                                                        ))}
                                                    </div>
                                                )}

                                            {msg.loanSimulationCard.jabaScoringDetail && (
                                                <div className="rounded-xl bg-slate-950/60 border border-indigo-400/25 p-2.5 space-y-1 text-[11px] text-slate-300">
                                                    <p className="font-extrabold text-indigo-300">
                                                        Evaluación Crediticia · {msg.loanSimulationCard.jabaScoringDetail.fullName}
                                                    </p>
                                                    <p>
                                                        • Puntaje Crediticio:{' '}
                                                        <strong className="text-white">
                                                            {msg.loanSimulationCard.jabaScoringDetail.scoreBcra ?? 'S/D'} pts
                                                        </strong>
                                                    </p>
                                                    <p>
                                                        • Ingreso Estimado:{' '}
                                                        <strong className="text-white">
                                                            $ {formatLocaleHumanAmount(msg.loanSimulationCard.jabaScoringDetail.ingresoCalculadoJabaHuman)} ARS
                                                        </strong>
                                                    </p>
                                                    <p>
                                                        • Cupo Mensual Estimado ({msg.loanSimulationCard.jabaScoringDetail.porcentajeOcupacionPct}%):{' '}
                                                        <strong className="text-white">
                                                            $ {formatLocaleHumanAmount(msg.loanSimulationCard.jabaScoringDetail.cupoDisponibleTeoricoHuman)} ARS
                                                        </strong>
                                                    </p>
                                                    <p>
                                                        • Compromisos informados:{' '}
                                                        <strong className="text-amber-300">
                                                            $ {formatLocaleHumanAmount(msg.loanSimulationCard.jabaScoringDetail.deudaTotalBcraHuman)} ARS
                                                        </strong>
                                                    </p>
                                                    <p>
                                                        • Límite Preaprobado Disponible:{' '}
                                                        <strong className="text-cyan-300">
                                                            $ {formatLocaleHumanAmount(msg.loanSimulationCard.jabaScoringDetail.limiteFinalJabaHuman)} ARS
                                                        </strong>
                                                    </p>
                                                </div>
                                            )}

                                            {msg.loanSimulationCard.eligible === false &&
                                                Array.isArray(msg.loanSimulationCard.missingFields) &&
                                                msg.loanSimulationCard.missingFields.length > 0 && (
                                                    <div className="rounded-xl bg-white/5 border border-white/15 p-2.5 space-y-2">
                                                        <p className="text-[11px] font-extrabold text-white">
                                                            Falta completar para habilitar tu línea:{' '}
                                                            {msg.loanSimulationCard.missingFields
                                                                .map((m) => m.label)
                                                                .join(', ')}
                                                        </p>
                                                        <p className="text-[10px] text-slate-300">
                                                            {msg.loanSimulationCard.missingFields[0].helperText ||
                                                                `Podés ingresarlo acá o decirme en el chat: "${msg.loanSimulationCard.missingFields[0].example || msg.loanSimulationCard.missingFields[0].placeholder}"`}
                                                        </p>
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={inlineBlockerInput}
                                                                onChange={(e) => setInlineBlockerInput(e.target.value)}
                                                                placeholder={
                                                                    msg.loanSimulationCard.missingFields[0].placeholder
                                                                }
                                                                className="flex-1 rounded-lg bg-black/40 border border-indigo-400/40 px-2.5 py-1.5 text-xs text-white placeholder-slate-400 focus:outline-none"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const val = inlineBlockerInput.trim();
                                                                    if (!val) return;
                                                                    const lbl =
                                                                        msg.loanSimulationCard?.missingFields?.[0]
                                                                            ?.label || 'dato';
                                                                    setInlineBlockerInput('');
                                                                    sendTurnToCopilot(`Mi ${lbl} es ${val}`);
                                                                }}
                                                                className="px-3 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white font-extrabold text-[11px]"
                                                            >
                                                                Guardar y Destrabar →
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            {msg.loanSimulationCard.eligible !== false &&
                                                msg.loanSimulationCard.plans.length === 0 && (
                                                    <div className="space-y-1.5">
                                                        <p className="text-[11px] font-bold text-slate-200">
                                                            Elegí el monto que querés acreditar:
                                                        </p>
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {[
                                                                '100000',
                                                                '250000',
                                                                String(
                                                                    msg.loanSimulationCard.availableCreditLimitArsHuman ||
                                                                        (msg.loanSimulationCard as any).availableLimitHuman ||
                                                                        '0'
                                                                ),
                                                            ]
                                                                .filter(
                                                                    (v, idx, arr) =>
                                                                        hasPositiveHumanAmount(v) &&
                                                                        arr.indexOf(v) === idx
                                                                )
                                                                .map((amt) => (
                                                                    <button
                                                                        key={amt}
                                                                        type="button"
                                                                        onClick={() =>
                                                                            sendTurnToCopilot(
                                                                                `Quiero solicitar $${formatLocaleHumanAmount(amt)} ARS en 6 cuotas`
                                                                            )
                                                                        }
                                                                        className="px-2.5 py-1.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-400/40 text-indigo-200 font-extrabold text-[11px]"
                                                                    >
                                                                        Solicitar ${formatLocaleHumanAmount(amt)} →
                                                                    </button>
                                                                ))}
                                                        </div>
                                                    </div>
                                                )}

                                            {msg.loanSimulationCard.eligible !== false &&
                                                msg.loanSimulationCard.plans.length > 0 && (
                                                    <>
                                                        <p className="text-[11px] text-slate-300">
                                                            Capital a acreditar:{' '}
                                                            <strong className="text-white">
                                                                $
                                                                {msg.loanSimulationCard.requestedPrincipalArsHuman ||
                                                                    (msg.loanSimulationCard as any)
                                                                        .requestedCapitalHuman ||
                                                                    '0.00'}{' '}
                                                                ARS
                                                            </strong>{' '}
                                                            {msg.loanSimulationCard.tnaPercent
                                                                ? `(TNA ${msg.loanSimulationCard.tnaPercent}%)`
                                                                : ''}
                                                        </p>
                                                        <div className="space-y-1.5">
                                                            {msg.loanSimulationCard.plans.map((plan) => (
                                                                <div
                                                                    key={plan.installments}
                                                                    className={`flex items-center justify-between p-2 rounded-xl border text-xs ${
                                                                        plan.isRecommended
                                                                            ? 'bg-indigo-500/20 border-indigo-400/50 text-white'
                                                                            : 'bg-white/5 border-white/10 text-slate-200'
                                                                    }`}
                                                                >
                                                                    <div>
                                                                        <span className="font-bold">
                                                                            {plan.installments} cuotas fijas
                                                                        </span>
                                                                        {plan.isRecommended && (
                                                                            <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 font-bold">
                                                                                ✨ Ideal para vos
                                                                            </span>
                                                                        )}
                                                                        <p className="text-[10px] text-slate-400">
                                                                            Total: $
                                                                            {plan.totalRepaymentArsHuman ||
                                                                                (plan as any).totalRepaymentHuman}{' '}
                                                                            · CFT Est.{' '}
                                                                            {plan.cftEstimatedPercent ||
                                                                                (plan as any).cfteaPct}
                                                                            %
                                                                        </p>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <span className="text-xs font-extrabold text-emerald-300 block">
                                                                            $
                                                                            {plan.monthlyInstallmentArsHuman ||
                                                                                (plan as any).monthlyInstallmentHuman}
                                                                            /mes
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setIsOpen(false);
                                                                                router.push('/wallet/loans');
                                                                            }}
                                                                            className="text-[10px] text-cyan-300 hover:underline font-bold"
                                                                        >
                                                                            Acreditar →
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            <p className="pt-1 text-center text-[9.5px] font-semibold text-slate-400/80">
                                                {copilotName} es una IA y puede cometer errores.
                                            </p>
                                        </div>
                                    )}

                                    {/* Tarjeta Interactiva de Pago Preparado + Just-in-Time Auto-Swap */}
                                    {msg.preparedPaymentCard && (
                                        <div className="mt-2.5 w-full max-w-[92%] rounded-2xl bg-gradient-to-br from-slate-900 via-emerald-950/50 to-slate-900 border border-emerald-400/35 p-3.5 shadow-xl">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-[11px] font-extrabold text-emerald-300 flex items-center gap-1.5">
                                                    <ShieldCheck className="w-4 h-4" />
                                                    Orden Preparada por {copilotName}
                                                </span>
                                                <span className="text-[9px] px-2 py-0.5 rounded-full bg-white/10 text-slate-300 font-mono">
                                                    {msg.preparedPaymentCard.rail}
                                                </span>
                                            </div>

                                            <div className="space-y-1 text-xs bg-black/30 rounded-xl p-2.5 border border-white/10 mb-2.5">
                                                <div className="flex justify-between">
                                                    <span className="text-slate-400">Destinatario:</span>
                                                    <span className="font-bold text-white">
                                                        {msg.preparedPaymentCard.recipientName} (
                                                        {msg.preparedPaymentCard.recipientAliasOrCvu})
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-400">Importe a enviar:</span>
                                                    <span className="font-extrabold text-emerald-300">
                                                        ${msg.preparedPaymentCard.requiredAmountHuman}{' '}
                                                        {msg.preparedPaymentCard.targetCurrency}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-slate-400">Concepto:</span>
                                                    <span className="text-slate-200">
                                                        {msg.preparedPaymentCard.concept}
                                                    </span>
                                                </div>

                                                {msg.preparedPaymentCard.needsAutoSwap && (
                                                    <div className="mt-2 pt-2 border-t border-amber-400/25 text-[11px] text-amber-200 space-y-1">
                                                        <div className="flex items-center gap-1 font-bold">
                                                            <ArrowRightLeft className="w-3.5 h-3.5 text-amber-300" />
                                                            Auto-Swap Atómico Just-in-Time
                                                        </div>
                                                        <p className="text-[10px] text-amber-100/90">
                                                            Tu saldo actual es $
                                                            {msg.preparedPaymentCard.currentTargetBalanceHuman}{' '}
                                                            {msg.preparedPaymentCard.targetCurrency}. Se convertirán{' '}
                                                            <strong>
                                                                {msg.preparedPaymentCard.swapSourceAmountRequiredHuman}{' '}
                                                                {msg.preparedPaymentCard.swapSourceCurrency}
                                                            </strong>{' '}
                                                            (Cotización ${msg.preparedPaymentCard.appliedFxRateHuman}) para
                                                            cubrir los ${msg.preparedPaymentCard.missingTargetAmountHuman}{' '}
                                                            restantes en un único asiento raíz.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>

                                            <button
                                                type="button"
                                                disabled={isExecutingPayment}
                                                onClick={() =>
                                                    handleExecutePreparedPayment(msg.preparedPaymentCard!)
                                                }
                                                className="w-full py-2.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 font-extrabold text-xs flex items-center justify-center gap-2 transition-all shadow-lg"
                                            >
                                                {isExecutingPayment ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        Procesando pago...
                                                    </>
                                                ) : (
                                                    <>
                                                        <CheckCircle2 className="w-4 h-4" />
                                                        {msg.preparedPaymentCard.needsAutoSwap
                                                            ? `Convertir ${msg.preparedPaymentCard.swapSourceAmountRequiredHuman} ${msg.preparedPaymentCard.swapSourceCurrency} y Pagar $${msg.preparedPaymentCard.requiredAmountHuman}`
                                                            : `Confirmar y Transferir $${msg.preparedPaymentCard.requiredAmountHuman} ${msg.preparedPaymentCard.targetCurrency}`}
                                                    </>
                                                )}
                                            </button>
                                            <p className="mt-2 text-center text-[9.5px] font-semibold text-slate-400/80">
                                                {copilotName} es una IA y puede cometer errores.
                                            </p>
                                        </div>
                                    )}

                                    {/* Tarjeta Interactiva de Reporte & Comparativa Multi-Período (Ledger V5) */}
                                    {msg.financialReportCard && (
                                        <div
                                            className={`relative mt-3 w-[95%] overflow-hidden rounded-[26px] border p-4 space-y-3.5 backdrop-blur-2xl transition-all ${
                                                isLightMode
                                                    ? 'bg-gradient-to-br from-white/95 via-white/75 to-sky-50/70 border-white/90 shadow-[0_18px_42px_-12px_rgba(2,132,199,0.22),inset_0_1.5px_1px_rgba(255,255,255,0.98)]'
                                                    : 'bg-gradient-to-br from-white/15 via-slate-900/65 to-cyan-950/45 border-white/25 shadow-[0_20px_48px_-12px_rgba(0,0,0,0.65),inset_0_1.5px_1px_rgba(255,255,255,0.35)]'
                                            }`}
                                        >
                                            {/* Top Specular LiquidGlass Rim Highlight */}
                                            <div className="pointer-events-none absolute inset-x-4 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-white to-transparent opacity-95" />

                                            {/* Header */}
                                            <div className="flex items-center justify-between gap-2.5">
                                                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                                                    <div
                                                        className={`w-8 h-8 rounded-[11px] flex items-center justify-center shrink-0 border backdrop-blur-md ${
                                                            isLightMode
                                                                ? 'bg-white/90 border-white shadow-[0_4px_12px_rgba(2,132,199,0.16),inset_0_1px_0_rgba(255,255,255,1)]'
                                                                : 'bg-white/15 border-white/30 shadow-inner'
                                                        }`}
                                                    >
                                                        <Sparkles
                                                            className="w-4 h-4 shrink-0"
                                                            style={{ color: primaryHex }}
                                                        />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p
                                                            className="text-[12.5px] font-black leading-tight truncate"
                                                            style={{ color: primaryHex }}
                                                        >
                                                            📊 Reporte &amp; Comparativa en Vivo
                                                        </p>
                                                        <p
                                                            className={`text-[9.5px] font-extrabold uppercase tracking-wider mt-0.5 ${
                                                                isLightMode ? 'text-slate-500' : 'text-slate-300'
                                                            }`}
                                                        >
                                                            RESUMEN DE TUS MOVIMIENTOS EN VIVO
                                                        </p>
                                                    </div>
                                                </div>
                                                <span
                                                    className={`text-[11px] font-black px-2.5 py-1 rounded-full border shrink-0 backdrop-blur-md ${
                                                        isLightMode
                                                            ? 'bg-white/90 border-white text-slate-900 shadow-[0_4px_10px_rgba(15,23,42,0.08),inset_0_1px_0_rgba(255,255,255,1)]'
                                                            : 'bg-white/15 border-white/30 text-white'
                                                    }`}
                                                >
                                                    ${msg.financialReportCard.portfolioValuationUsdHuman} USD
                                                </span>
                                            </div>

                                            {/* Two Ultra LiquidGlass Bento Comparison Pods */}
                                            <div className="grid grid-cols-2 gap-2.5">
                                                <div
                                                    className={`relative overflow-hidden rounded-[18px] border p-3 space-y-1 backdrop-blur-xl ${
                                                        isLightMode
                                                            ? 'bg-gradient-to-br from-white/90 via-emerald-50/65 to-teal-100/45 border-white/95 shadow-[0_8px_20px_-6px_rgba(16,185,129,0.18),inset_0_1px_0_rgba(255,255,255,0.95)]'
                                                            : 'bg-gradient-to-br from-emerald-500/20 via-slate-900/60 to-teal-900/30 border-emerald-400/35'
                                                    }`}
                                                >
                                                    <p className="text-[10.5px] font-extrabold text-emerald-600 dark:text-emerald-400">
                                                        {msg.financialReportCard.currentMonth.periodLabel}
                                                    </p>
                                                    <p
                                                        className={`text-[17px] font-black tracking-tight ${
                                                            isLightMode ? 'text-slate-900' : 'text-white'
                                                        }`}
                                                    >
                                                        {msg.financialReportCard.currentMonth.totalOperationsCount} movs
                                                    </p>
                                                    <div
                                                        className={`inline-flex items-center rounded-lg px-2 py-0.5 border text-[10px] font-bold ${
                                                            isLightMode
                                                                ? 'bg-white/80 border-white/90 text-slate-700'
                                                                : 'bg-white/10 border-white/20 text-slate-200'
                                                        }`}
                                                    >
                                                        ↗ {msg.financialReportCard.currentMonth.outgoingTransfersCount}{' '}
                                                        envíos · ↙{' '}
                                                        {msg.financialReportCard.currentMonth.incomingTransfersCount +
                                                            msg.financialReportCard.currentMonth
                                                                .externalDepositsCount}{' '}
                                                        ingresos
                                                    </div>
                                                </div>

                                                <div
                                                    className={`relative overflow-hidden rounded-[18px] border p-3 space-y-1 backdrop-blur-xl ${
                                                        isLightMode
                                                            ? 'bg-gradient-to-br from-white/90 via-sky-50/55 to-indigo-100/35 border-white/95 shadow-[0_8px_20px_-6px_rgba(14,165,233,0.14),inset_0_1px_0_rgba(255,255,255,0.95)]'
                                                            : 'bg-gradient-to-br from-sky-500/15 via-slate-900/60 to-indigo-950/35 border-white/20'
                                                    }`}
                                                >
                                                    <p
                                                        className={`text-[10.5px] font-extrabold ${
                                                            isLightMode ? 'text-slate-600' : 'text-slate-300'
                                                        }`}
                                                    >
                                                        {msg.financialReportCard.previousMonth.periodLabel}
                                                    </p>
                                                    <p
                                                        className={`text-[17px] font-black tracking-tight ${
                                                            isLightMode ? 'text-slate-900' : 'text-white'
                                                        }`}
                                                    >
                                                        {msg.financialReportCard.previousMonth.totalOperationsCount}{' '}
                                                        movs
                                                    </p>
                                                    <div
                                                        className={`inline-flex items-center rounded-lg px-2 py-0.5 border text-[10px] font-bold ${
                                                            isLightMode
                                                                ? 'bg-white/80 border-white/90 text-slate-700'
                                                                : 'bg-white/10 border-white/20 text-slate-200'
                                                        }`}
                                                    >
                                                        ↗{' '}
                                                        {msg.financialReportCard.previousMonth.outgoingTransfersCount}{' '}
                                                        envíos · ↙{' '}
                                                        {msg.financialReportCard.previousMonth.incomingTransfersCount +
                                                            msg.financialReportCard.previousMonth
                                                                .externalDepositsCount}{' '}
                                                        ingresos
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Multi-Asset Ultra LiquidGlass Rows */}
                                            <div className="space-y-2">
                                                {msg.financialReportCard.currencyBreakdowns.slice(0, 3).map((cb) => {
                                                    const cleanLabel =
                                                        cb.displayName &&
                                                        cb.displayName.toUpperCase() !== cb.currency.toUpperCase()
                                                            ? `${cb.displayName} (${cb.currency})`
                                                            : cb.currency === 'ETH'
                                                              ? 'Ethereum (ETH)'
                                                              : cb.currency === 'USDT'
                                                                ? 'Tether USD (USDT)'
                                                                : cb.currency === 'ARS'
                                                                  ? 'Pesos (ARS)'
                                                                  : cb.currency;
                                                    return (
                                                        <div
                                                            key={cb.currency}
                                                            className={`relative overflow-hidden rounded-[18px] border px-3 py-2.5 text-[11px] space-y-1 backdrop-blur-xl ${
                                                                isLightMode
                                                                    ? 'bg-gradient-to-r from-white/90 via-white/75 to-sky-50/55 border-white/95 shadow-[0_6px_16px_-6px_rgba(15,23,42,0.08),inset_0_1px_0_rgba(255,255,255,0.95)]'
                                                                    : 'bg-gradient-to-r from-white/12 via-slate-900/55 to-cyan-950/35 border-white/20'
                                                            }`}
                                                        >
                                                            <div className="flex items-center justify-between gap-2 font-extrabold">
                                                                <div className="flex items-center gap-2 min-w-0">
                                                                    <span
                                                                        className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-md text-[9.5px] font-black border ${
                                                                            isLightMode
                                                                                ? 'bg-white border-slate-200/80 shadow-xs'
                                                                                : 'bg-white/15 border-white/25'
                                                                        }`}
                                                                        style={{ color: primaryHex }}
                                                                    >
                                                                        {cb.currency}
                                                                    </span>
                                                                    <span
                                                                        className={`truncate font-black ${
                                                                            isLightMode ? 'text-slate-900' : 'text-white'
                                                                        }`}
                                                                    >
                                                                        {cleanLabel}
                                                                    </span>
                                                                </div>
                                                                <span
                                                                    className={`px-2 py-0.5 rounded-full border text-[10.5px] font-black shrink-0 ${
                                                                        isLightMode
                                                                            ? 'bg-white/90 border-white shadow-xs'
                                                                            : 'bg-white/10 border-white/25'
                                                                    }`}
                                                                    style={{ color: primaryHex }}
                                                                >
                                                                    Disp: {cb.currentBalanceMainHuman} {cb.currency}
                                                                </span>
                                                            </div>
                                                            <p
                                                                className={`text-[10.5px] font-semibold ${
                                                                    isLightMode ? 'text-slate-600' : 'text-slate-300'
                                                                }`}
                                                            >
                                                                • Este mes: {cb.currentMonthSentCount} envíos (-
                                                                {cb.currentMonthSentHuman} {cb.currency}) ·{' '}
                                                                {cb.currentMonthReceivedCount} ingresos (+
                                                                {cb.currentMonthReceivedHuman} {cb.currency})
                                                            </p>
                                                            <p
                                                                className={`text-[10.5px] font-semibold ${
                                                                    isLightMode ? 'text-slate-500' : 'text-slate-400'
                                                                }`}
                                                            >
                                                                • Mes pasado: {cb.previousMonthSentCount} envíos (-
                                                                {cb.previousMonthSentHuman} {cb.currency}) ·{' '}
                                                                {cb.previousMonthReceivedCount} ingresos (+
                                                                {cb.previousMonthReceivedHuman} {cb.currency})
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Proactive Recommendations */}
                                            {Array.isArray(msg.financialReportCard.proactiveRecommendations) &&
                                                msg.financialReportCard.proactiveRecommendations.length > 0 && (
                                                    <div className="space-y-2 pt-1">
                                                        <div className="flex items-center gap-1.5">
                                                            <span
                                                                className={`inline-flex items-center justify-center w-5 h-5 rounded-md border text-[11px] ${
                                                                    isLightMode
                                                                        ? 'bg-white/90 border-white shadow-xs'
                                                                        : 'bg-white/15 border-white/25'
                                                                }`}
                                                            >
                                                                💡
                                                            </span>
                                                            <p
                                                                className="text-[11.5px] font-black"
                                                                style={{ color: primaryHex }}
                                                            >
                                                                Sugerencias de {copilotName} hacia el Futuro
                                                            </p>
                                                        </div>
                                                        {msg.financialReportCard.proactiveRecommendations
                                                            .slice(0, 2)
                                                            .map((rec) => (
                                                                <div
                                                                    key={rec.id}
                                                                    className={`relative overflow-hidden rounded-[18px] border p-3 space-y-2 backdrop-blur-xl ${
                                                                        isLightMode
                                                                            ? 'bg-gradient-to-br from-white/92 via-white/75 to-sky-50/60 border-white/95 shadow-[0_8px_20px_-6px_rgba(2,132,199,0.14),inset_0_1px_0_rgba(255,255,255,0.95)]'
                                                                            : 'bg-gradient-to-br from-white/12 via-slate-900/60 to-cyan-950/35 border-white/20'
                                                                    }`}
                                                                >
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <span
                                                                            className={`text-[11.5px] font-black ${
                                                                                isLightMode
                                                                                    ? 'text-slate-900'
                                                                                    : 'text-white'
                                                                            }`}
                                                                        >
                                                                            {rec.title}
                                                                        </span>
                                                                        <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 shrink-0">
                                                                            {rec.impactHighlight}
                                                                        </span>
                                                                    </div>
                                                                    <p
                                                                        className={`text-[10.5px] leading-snug font-medium ${
                                                                            isLightMode
                                                                                ? 'text-slate-600'
                                                                                : 'text-slate-300'
                                                                        }`}
                                                                    >
                                                                        {rec.rationale}
                                                                    </p>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            sendTurnToCopilot(rec.actionPrompt)
                                                                        }
                                                                        className="relative overflow-hidden text-[11px] font-black text-white px-3.5 py-1.5 rounded-[12px] shadow-md transition-transform active:scale-[0.98]"
                                                                        style={{ backgroundColor: primaryHex }}
                                                                    >
                                                                        <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent" />
                                                                        <span className="relative z-10">
                                                                            {rec.actionLabel} →
                                                                        </span>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                            <p
                                                className={`pt-0.5 text-center text-[9.5px] font-semibold opacity-75 ${
                                                    isLightMode ? 'text-slate-500' : 'text-slate-400'
                                                }`}
                                            >
                                                {copilotName} es una IA y puede cometer errores.
                                            </p>
                                        </div>
                                    )}

                                    {/* Chips de Desambiguación y Sugerencias 1-Tap */}
                                    {msg.suggestedFollowUps && msg.suggestedFollowUps.length > 0 && (
                                        <div className="mt-2.5 flex flex-wrap gap-2 max-w-[92%]">
                                            {msg.suggestedFollowUps.map((chip, idx) => (
                                                <button
                                                    key={`${msg.id}-chip-${idx}`}
                                                    type="button"
                                                    onClick={() => {
                                                        if (
                                                            chip.includes('📷') ||
                                                            chip.toLowerCase().includes('escanear')
                                                        ) {
                                                            cameraInputRef.current?.click();
                                                        } else if (
                                                            chip.toLowerCase().includes('subir captura') ||
                                                            chip.toLowerCase().includes('pdf')
                                                        ) {
                                                            fileInputRef.current?.click();
                                                        } else if (chip.toLowerCase().includes('aprendé más de max')) {
                                                            setIsOpen(false);
                                                            router.push('/wallet/profile/agent-max');
                                                        } else {
                                                            sendTurnToCopilot(chip);
                                                        }
                                                    }}
                                                    className={`text-[11px] px-3 py-1.5 rounded-full border font-extrabold backdrop-blur-md transition-all ${
                                                        isLightMode
                                                            ? 'bg-white/85 hover:bg-white shadow-sm'
                                                            : 'bg-white/10 hover:bg-white/15 border-white/25 text-cyan-200'
                                                    }`}
                                                    style={
                                                        isLightMode
                                                            ? {
                                                                  borderColor: `${primaryHex}40`,
                                                                  color: primaryHex,
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    ✨ {chip}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {isLoading && (
                                <div
                                    className={`flex items-center gap-2 text-xs px-3.5 py-2.5 rounded-2xl w-fit border backdrop-blur-md ${
                                        isLightMode
                                            ? 'bg-white/85 text-slate-700 border-white shadow-sm'
                                            : 'bg-white/10 text-slate-200 border-white/15'
                                    }`}
                                >
                                    <Sparkles
                                        className="w-4 h-4 animate-pulse"
                                        style={{ color: primaryHex }}
                                    />
                                    <span className="font-bold">
                                        Echando un vistazo a &quot;{screenMeta.title}&quot;...
                                    </span>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Preview de Imagen o PDF Adjunto antes de Enviar */}
                        {attachedImagePreview && (
                            <div
                                className={`px-4 py-2 border-t flex items-center justify-between backdrop-blur-xl ${
                                    isLightMode
                                        ? 'bg-white/80 border-slate-200/80'
                                        : 'bg-slate-900/90 border-white/15'
                                }`}
                            >
                                <div className="flex items-center gap-2.5">
                                    {attachedImageMime === 'application/pdf' ? (
                                        <div
                                            className="w-10 h-10 rounded-lg border flex items-center justify-center"
                                            style={{
                                                backgroundColor: `${primaryHex}15`,
                                                borderColor: `${primaryHex}40`,
                                            }}
                                        >
                                            <FileText
                                                className="w-5 h-5"
                                                style={{ color: primaryHex }}
                                            />
                                        </div>
                                    ) : (
                                        <img
                                            src={attachedImagePreview}
                                            alt="Preview"
                                            className="w-10 h-10 rounded-lg object-cover border"
                                            style={{ borderColor: `${primaryHex}50` }}
                                        />
                                    )}
                                    <div className="text-[11px]">
                                        <p className="font-extrabold" style={{ color: primaryHex }}>
                                            {attachedImageMime === 'application/pdf'
                                                ? `PDF listo: ${attachedFileName || 'Documento.pdf'}`
                                                : 'Captura lista para analizar'}
                                        </p>
                                        <p
                                            className={`text-[10px] ${
                                                isLightMode ? 'text-slate-500' : 'text-slate-400'
                                            }`}
                                        >
                                            {copilotName} extraerá Alias/CVU, monto y concepto
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setAttachedImageBase64(null);
                                        setAttachedImagePreview(null);
                                        setAttachedFileName(null);
                                    }}
                                    className={`p-1.5 rounded-lg ${
                                        isLightMode
                                            ? 'bg-slate-200/70 text-slate-700 hover:bg-slate-200'
                                            : 'bg-white/10 text-slate-300 hover:text-white'
                                    }`}
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}

                        {/* Cinta Reactiva de Sugerencias Contextuales por Pestaña Activa */}
                        {messages.some((m) => m.role === 'user') && screenMeta.quickPrompts.length > 0 && (
                            <div
                                className={`px-3.5 py-2 border-t flex items-center gap-1.5 overflow-x-auto no-scrollbar backdrop-blur-xl ${
                                    isLightMode
                                        ? 'bg-white/60 border-slate-200/70'
                                        : 'bg-white/5 border-white/10'
                                }`}
                            >
                                <span
                                    className="text-[10px] font-black uppercase tracking-wider shrink-0 mr-1"
                                    style={{ color: primaryHex }}
                                >
                                    En esta pestaña:
                                </span>
                                {screenMeta.quickPrompts.map((promptChip, idx) => (
                                    <button
                                        key={`ctx-strip-${idx}-${promptChip}`}
                                        type="button"
                                        onClick={() => {
                                            if (
                                                promptChip.includes('📷') ||
                                                promptChip.toLowerCase().includes('escanear')
                                            ) {
                                                cameraInputRef.current?.click();
                                            } else {
                                                sendTurnToCopilot(promptChip);
                                            }
                                        }}
                                        className={`text-[10.5px] px-2.5 py-1 rounded-full border font-extrabold shrink-0 transition-all ${
                                            isLightMode
                                                ? 'bg-white/90 hover:bg-white border-slate-200 text-slate-700 shadow-xs'
                                                : 'bg-white/10 hover:bg-white/15 border-white/20 text-cyan-200'
                                        }`}
                                        style={
                                            isLightMode
                                                ? {
                                                      borderColor: `${primaryHex}35`,
                                                      color: primaryHex,
                                                  }
                                                : undefined
                                        }
                                    >
                                        ✨ {promptChip}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Pill Estilo Gemini: Compartiendo pantalla amigable + Acceso rápido a Misiones */}
                        <div
                            className={`px-3.5 py-2 border-t flex items-center justify-between gap-2 backdrop-blur-xl ${
                                isLightMode
                                    ? 'bg-white/70 border-slate-200/70'
                                    : 'bg-white/5 border-white/10'
                            }`}
                        >
                            {shareScreenContext ? (
                                <div
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold ${
                                        isLightMode
                                            ? 'bg-white/90 border-slate-200/90 text-slate-700 shadow-xs'
                                            : 'bg-white/10 border-white/20 text-slate-200'
                                    }`}
                                >
                                    <Compass className="w-3.5 h-3.5" style={{ color: primaryHex }} />
                                    <span className="truncate max-w-[210px]">
                                        Compartiendo &quot;{screenMeta.title}&quot;
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setShareScreenContext(false)}
                                        title="Dejar de compartir contexto de esta pantalla"
                                        className="ml-0.5 opacity-60 hover:opacity-100"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setShareScreenContext(true)}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold ${
                                        isLightMode
                                            ? 'bg-white/80 border-slate-200 text-slate-600'
                                            : 'bg-white/8 border-white/15 text-slate-300'
                                    }`}
                                >
                                    <Compass className="w-3.5 h-3.5" style={{ color: primaryHex }} />
                                    <span className="truncate max-w-[210px]">
                                        Compartir &quot;{screenMeta.title}&quot;
                                    </span>
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={() => {
                                    setIsOpen(false);
                                    if (screenMeta.portalRole === 'MASTER') {
                                        router.push('/master/agents');
                                    } else if (screenMeta.portalRole === 'MERCHANT') {
                                        router.push('/merchant/wallets');
                                    } else {
                                        router.push('/wallet/profile/agent-max');
                                    }
                                }}
                                className="text-[11px] font-extrabold hover:underline shrink-0"
                                style={{ color: primaryHex }}
                            >
                                {screenMeta.portalRole === 'MASTER'
                                    ? '✨ Consola de Agentes'
                                    : screenMeta.portalRole === 'MERCHANT'
                                    ? '✨ Capital & Billeteras'
                                    : `✨ Misiones de ${copilotName}`}
                            </button>
                        </div>

                        {/* Barra de Entrada Multimodal 100% LiquidGlass (Texto + Captura/PDF + Micrófono) */}
                        <div
                            className={`p-3 border-t flex items-center gap-2 backdrop-blur-2xl ${
                                isLightMode
                                    ? 'bg-white/78 border-white'
                                    : 'bg-[#0c1220]/80 border-white/15'
                            }`}
                        >
                            <input
                                ref={cameraInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageFileSelect(file);
                                    e.target.value = '';
                                }}
                            />
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,application/pdf,.pdf"
                                className="hidden"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleImageFileSelect(file);
                                    e.target.value = '';
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => cameraInputRef.current?.click()}
                                title="Activar cámara para escanear un Alias o datos de banco"
                                data-testid="max-copilot-camera"
                                className={`p-2.5 rounded-2xl border transition-colors shrink-0 backdrop-blur-md ${
                                    isLightMode
                                        ? 'bg-white/90 hover:bg-white border-slate-200/90 shadow-sm'
                                        : 'bg-white/8 hover:bg-white/15 border-white/15'
                                }`}
                                style={{ color: primaryHex }}
                            >
                                <Camera className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                title="Subir captura de pantalla o factura PDF (WhatsApp, boleta, alias)"
                                className={`p-2.5 rounded-2xl border transition-colors shrink-0 backdrop-blur-md ${
                                    isLightMode
                                        ? 'bg-white/90 hover:bg-white border-slate-200/90 shadow-sm'
                                        : 'bg-white/8 hover:bg-white/15 border-white/15'
                                }`}
                                style={{ color: primaryHex }}
                            >
                                <ImageIcon className="w-4 h-4" />
                            </button>

                            <button
                                type="button"
                                onClick={toggleMicrophone}
                                title="Hablar con Max (es-AR)"
                                className={`p-2.5 rounded-2xl border transition-colors shrink-0 backdrop-blur-md ${
                                    isListeningMic
                                        ? 'bg-rose-500/20 border-rose-400 text-rose-500 animate-pulse'
                                        : isLightMode
                                        ? 'bg-white/90 hover:bg-white border-slate-200/90 shadow-sm'
                                        : 'bg-white/8 hover:bg-white/15 border-white/15 text-amber-300'
                                }`}
                                style={!isListeningMic && isLightMode ? { color: primaryHex } : undefined}
                            >
                                {isListeningMic ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                            </button>

                            <textarea
                                rows={2}
                                data-testid="max-copilot-input"
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        sendTurnToCopilot();
                                    }
                                }}
                                placeholder={`Preguntale a ${copilotName} sobre ${screenMeta.title.toLowerCase()}...`}
                                className={`flex-1 min-w-0 resize-none rounded-2xl px-3.5 py-2 text-xs leading-snug font-semibold border focus:outline-none transition-colors ${
                                    isLightMode
                                        ? 'bg-white/95 border-slate-200 text-slate-900 placeholder:text-slate-400'
                                        : 'bg-white/8 border-white/15 text-white placeholder:text-slate-400'
                                }`}
                            />

                            <button
                                type="button"
                                data-testid="max-copilot-send"
                                disabled={isLoading || (!inputMessage.trim() && !attachedImageBase64)}
                                onClick={() => sendTurnToCopilot()}
                                className="p-2.5 rounded-2xl border border-white/40 disabled:opacity-40 text-white shadow-md transition-all shrink-0"
                                style={{
                                    backgroundColor: primaryHex,
                                    backgroundImage:
                                        'linear-gradient(135deg, rgba(255,255,255,0.28) 0%, transparent 100%)',
                                }}
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                        <div
                            className={`pb-2.5 pt-0.5 px-3 text-center text-[9.5px] font-semibold backdrop-blur-2xl ${
                                isLightMode
                                    ? 'bg-white/78 text-slate-500/85'
                                    : 'bg-[#0c1220]/80 text-slate-400/80'
                            }`}
                        >
                            {copilotName} es una IA y puede cometer errores.
                        </div>
                        </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
